import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';
import { ProductsManagement, OrdersManagement, CustomersManagement, ShippingSettings, CategoryManagement } from './components/AdminComponents';
import { OrderNumberManagement, AnalyticsDashboard, EnhancedOrdersManagement, MarketingManagement, EmailTemplatesManagement, NewCustomerSettingsManagement } from './components/AdminExtensions';
import { AIProductsManager } from './components/AIProductsManager';
import { ProductsCatalogSection, OrdersFulfillmentSection, MarketingCommsSection, WebsiteSection, SettingsSection, DashboardSection } from './components/AdminSections';
import { NotificationsDropdown } from './components/NotificationsCenter';
import { AccountCompletion, CustomerAccount } from './components/CustomerAccount';
import PaymentSuccess from './components/PaymentSuccess';
import PayPalCheckout from './components/PayPalCheckout';
import { ImagePreviewModal, ImageThumbnail } from './components/ImagePreview';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { MaterialsManagement, CapabilitiesManagement } from './components/MaterialsCapabilities';
import { WebsiteCustomizationManagement } from './components/WebsiteCustomization';
import { ContactFormModal } from './components/ContactFormModal';
import { PolicyPage } from './components/PolicyPage';
import { CookieConsent } from './components/CookieConsent';
import { MaintenancePage } from './components/MaintenancePage';
import PremiumHomePage from './components/PremiumHomePage';
import AIProductsPage from './components/AIProductsPage';
import LithophaneSpherePage from './components/LithophaneSpherePage';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import FileManager from './components/FileManager';
import './components/ProductDescription.css';
import { EnhancedProductCard } from './components/EnhancedProductCard';

// ScrollToTop component - scrolls to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Configure axios
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
axios.defaults.baseURL = BACKEND_URL;
axios.defaults.withCredentials = true;

// Helper function to construct proper image URLs with fallback support
const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http')) return imagePath; // External URL
  
  // Handle GridFS images (new persistent storage)
  if (imagePath.startsWith('/api/images/')) {
    // GridFS images - persistent storage that survives deployments
    return `${BACKEND_URL}${imagePath}`;
  }
  
  // Handle various legacy upload path formats
  if (imagePath.startsWith('/api/uploads/')) {
    // If path uses /api/uploads/, provide fallback to /api/files/
    const filename = imagePath.split('/').pop();
    return `${BACKEND_URL}/api/files/${filename}`;  // Use /api/files/ instead for better reliability
  }
  
  if (imagePath.startsWith('/api/files/')) {
    // Already using /api/files/, use as-is
    return `${BACKEND_URL}${imagePath}`;
  }
  
  if (imagePath.startsWith('/uploads/') || imagePath.startsWith('/tmp/uploads/')) {
    // Extract filename and use API endpoint
    const filename = imagePath.split('/').pop();
    return `${BACKEND_URL}/api/files/${filename}`;
  }
  
  // Fallback: if it looks like a filename (UUID pattern), use files endpoint
  if (imagePath.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\.(png|jpg|jpeg|gif|webp)$/i)) {
    return `${BACKEND_URL}/api/files/${imagePath}`;
  }
  
  return imagePath;
};

// Helper function to format currency - Always displays Canadian dollars
const formatCurrency = (amount) => {
  // Always format as Canadian dollars (CAD)
  return `$${Number(amount).toFixed(2)} CAD`;
};

// Context for global state
const AppContext = createContext();

const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// AppProvider component
const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  useEffect(() => {
    // Check maintenance mode first
    checkMaintenanceMode();
    
    // Don't load cart from localStorage initially - wait for auth check
    // Load token from localStorage and set axios header
    const savedToken = localStorage.getItem('access_token');
    if (savedToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      console.log('Restored authentication token');
    }

    // Check authentication
    checkAuth();
  }, []);

  const checkMaintenanceMode = async () => {
    try {
      const response = await axios.get(`${API}/system/maintenance-status`);
      if (response.data.maintenance_mode) {
        setMaintenanceMode(true);
        setMaintenanceMessage(response.data.maintenance_message);
      }
    } catch (error) {
      console.error('Failed to check maintenance mode:', error);
      // If check fails, allow access
    }
  };

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data.user;
      setUser(userData);
      
      // User is logged in - load cart from database
      try {
        const cartResponse = await axios.get(`${API}/customers/cart`);
        if (cartResponse.data.success && cartResponse.data.cart.length > 0) {
          setCart(cartResponse.data.cart);
          localStorage.setItem('cart', JSON.stringify(cartResponse.data.cart));
          console.log('Loaded cart from database');
        } else {
          // No saved cart, start with empty cart
          setCart([]);
          localStorage.removeItem('cart');
        }
      } catch (cartError) {
        console.error('Failed to load cart from database:', cartError);
        // Fallback to empty cart if database load fails
        setCart([]);
        localStorage.removeItem('cart');
      }
    } catch (error) {
      console.log('Not authenticated - cart will be empty');
      // User is not logged in - clear cart
      setCart([]);
      localStorage.removeItem('cart');
    } finally {
      setLoading(false);
    }
  };

  const saveCartToDatabase = async (cartData) => {
    if (user) {
      try {
        await axios.put(`${API}/customers/cart`, { cart: cartData });
        console.log('Cart saved to database');
      } catch (error) {
        console.error('Failed to save cart to database:', error);
      }
    }
  };

  const addToCart = (item) => {
    if (!user) {
      toast.error('Please log in to add items to cart');
      return;
    }

    setCart(prevCart => {
      const existingItemIndex = prevCart.findIndex(cartItem => 
        cartItem.product_id === item.product_id &&
        JSON.stringify(cartItem.selected_options) === JSON.stringify(item.selected_options)
      );

      let newCart;
      if (existingItemIndex > -1) {
        newCart = [...prevCart];
        newCart[existingItemIndex].quantity += item.quantity;
      } else {
        newCart = [...prevCart, item];
      }

      localStorage.setItem('cart', JSON.stringify(newCart));
      saveCartToDatabase(newCart);
      return newCart;
    });
    
    toast.success('Added to cart!');
  };
  
  // Batch add multiple items to cart at once (prevents race conditions)
  const addMultipleToCart = (items) => {
    if (!user) {
      toast.error('Please log in to add items to cart');
      return;
    }
    
    setCart(prevCart => {
      let newCart = [...prevCart];
      
      items.forEach(item => {
        const existingItemIndex = newCart.findIndex(cartItem => 
          cartItem.product_id === item.product_id &&
          JSON.stringify(cartItem.selected_options) === JSON.stringify(item.selected_options)
        );

        if (existingItemIndex > -1) {
          newCart[existingItemIndex].quantity += item.quantity;
        } else {
          newCart.push(item);
        }
      });

      localStorage.setItem('cart', JSON.stringify(newCart));
      saveCartToDatabase(newCart);
      return newCart;
    });
    
    toast.success(`Added ${items.length} item${items.length > 1 ? 's' : ''} to cart!`);
  };

  const removeFromCart = (index) => {
    const newCart = cart.filter((_, i) => i !== index);
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
    if (user) {
      saveCartToDatabase(newCart);
    }
  };

  const clearCart = async () => {
    setCart([]);
    localStorage.removeItem('cart');
    if (user) {
      try {
        await axios.delete(`${API}/customers/cart`);
        console.log('Cart cleared from database');
      } catch (error) {
        console.error('Failed to clear cart from database:', error);
      }
    }
  };

  const getCartTotal = () => {
    // Only sum items that are not marked as included (price > 0)
    return cart.reduce((total, item) => {
      if (item.is_included) return total;
      return total + (item.price * item.quantity);
    }, 0);
  };

  const updateCartQuantity = (index, quantity) => {
    if (quantity <= 0) {
      removeFromCart(index);
      return;
    }

    const newCart = [...cart];
    newCart[index].quantity = quantity;
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
    if (user) {
      saveCartToDatabase(newCart);
    }
  };

  const login = async (userData, token) => {
    setUser(userData);
    localStorage.setItem('access_token', token);
    // Set axios default authorization header
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Load user's cart from database after login
    try {
      const cartResponse = await axios.get(`${API}/customers/cart`);
      if (cartResponse.data.success && cartResponse.data.cart.length > 0) {
        setCart(cartResponse.data.cart);
        localStorage.setItem('cart', JSON.stringify(cartResponse.data.cart));
        console.log('Loaded cart after login');
      } else {
        // No saved cart
        setCart([]);
        localStorage.removeItem('cart');
      }
    } catch (error) {
      console.error('Failed to load cart after login:', error);
      setCart([]);
    }
  };

  const logout = async () => {
    // Clear cart when logging out
    setCart([]);
    localStorage.removeItem('cart');
    localStorage.removeItem('access_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    
    // Call backend logout if endpoint exists
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.log('Logout API call failed, but local logout successful');
    }
  };

  return (
    <AppContext.Provider value={{
      user,
      setUser,
      cart,
      addToCart,
      addMultipleToCart,
      removeFromCart,
      clearCart,
      getCartTotal,
      updateCartQuantity,
      loading,
      setLoading,
      login,
      logout,
      maintenanceMode,
      maintenanceMessage
    }}>
      {children}
    </AppContext.Provider>
  );
};

// Enhanced Header Component with Integrated Logo and Color Scheme
const Header = () => {
  const { user, cart, setUser, logout } = useApp();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    loadCategories();
    loadSettings();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/public`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-[1000] shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Brand */}
          <div 
            className="flex items-center space-x-3 cursor-pointer"
            onClick={() => navigate('/')}
          >
            {settings?.company_settings?.logo_url && (
              <img 
                src={settings.company_settings.logo_url} 
                alt="RG-3D Logo" 
                className="w-10 h-10 object-contain"
              />
            )}
            <span className="text-xl font-bold text-gray-800">RG-3D</span>
          </div>

          {/* Navigation Links */}
          <nav className="hidden md:flex space-x-8">
            <button 
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-blue-600 transition-colors duration-200 font-medium"
            >
              Home
            </button>
            <button 
              onClick={() => navigate('/products')}
              className="relative text-gray-600 hover:text-blue-600 transition-colors duration-200 font-medium px-3 py-2 rounded-lg overflow-hidden group"
            >
              <span className="relative z-10">Products</span>
              {/* Animated shimmer effect - moving gradient */}
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/30 to-transparent shimmer-animation"></span>
              {/* Border highlight effect */}
              <span className="absolute inset-0 border-2 border-transparent group-hover:border-blue-400/50 rounded-lg transition-all duration-300"></span>
            </button>
            {/* AI Products - Available to all logged-in users */}
            {user && (
              <button 
                onClick={() => navigate('/ai-products')}
                className="relative text-gray-600 hover:text-purple-600 transition-colors duration-200 font-medium px-3 py-2 rounded-lg overflow-hidden group"
              >
                <span className="relative z-10 flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  AI Products
                </span>
                {/* Animated shimmer effect - moving gradient with purple tint */}
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-500/30 to-transparent shimmer-animation"></span>
                {/* Border highlight effect */}
                <span className="absolute inset-0 border-2 border-transparent group-hover:border-purple-400/50 rounded-lg transition-all duration-300"></span>
              </button>
            )}
            <div className="relative group">
              <button className="text-gray-600 hover:text-blue-600 transition-colors duration-200 font-medium">
                Categories
                <svg className="w-4 h-4 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                <div className="py-2">
                  {categories.length > 0 ? (
                    (() => {
                      // Get only parent categories
                      const parentCategories = categories.filter(c => !c.parent_id);
                      
                      return parentCategories.map((parentCat) => {
                        // Get children for this parent
                        const children = categories.filter(c => c.parent_id === parentCat.id);
                        const hasChildren = children.length > 0;
                        
                        return (
                          <div key={parentCat.id} className="relative group/parent">
                            {/* Parent Category */}
                            <button 
                              onClick={() => navigate(`/category/${parentCat.slug}`)}
                              className="block w-full text-left px-4 py-2.5 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200 font-medium flex items-center justify-between"
                            >
                              <span>{parentCat.name}</span>
                              {hasChildren && (
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              )}
                            </button>
                            
                            {/* Sub-categories - Show on hover */}
                            {hasChildren && (
                              <div className="absolute left-full top-0 ml-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover/parent:opacity-100 group-hover/parent:visible transition-all duration-200 z-20">
                                <div className="py-2">
                                  {children.map((child) => (
                                    <button 
                                      key={child.id}
                                      onClick={() => navigate(`/category/${parentCat.slug}/${child.slug}`)}
                                      className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200"
                                    >
                                      {child.name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()
                  ) : (
                    <div className="px-4 py-2 text-gray-500 text-sm">
                      No categories available
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Contact Button - Only for logged-in users */}
            {user && (
              <button 
                onClick={() => setShowContactModal(true)}
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200 text-sm font-normal"
              >
                Contact
              </button>
            )}
          </nav>

          {/* Right side actions */}
          <div className="flex items-center space-x-4">
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="text-gray-600 hover:text-blue-600 transition-colors duration-200 p-2 rounded-full hover:bg-gray-100"
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDark ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            
            <button 
              onClick={() => navigate('/cart')}
              className="text-gray-600 hover:text-blue-600 relative transition-colors duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m-2.4 0L7 13m0 0v6a2 2 0 002 2h6a2 2 0 002-2v-6M7 13l-2-8" />
              </svg>
              {cart.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {cart.length}
                </span>
              )}
            </button>
            
            {user ? (
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => navigate('/account')}
                  className="text-gray-600 hover:text-blue-600 transition-colors duration-200 font-medium"
                >
                  My Account
                </button>
                <span className="text-gray-700">Hello, {user?.name || user?.first_name || user?.email || 'User'}</span>
                {user?.is_admin && (
                  <button 
                    onClick={() => navigate('/admin')}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-full text-sm transition-colors duration-200"
                  >
                    Admin
                  </button>
                )}
                <button 
                  onClick={handleLogout}
                  className="text-gray-600 hover:text-red-600 transition-colors duration-200"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => navigate('/login')}
                  className="text-gray-600 hover:text-blue-600 transition-colors duration-200 font-medium"
                >
                  Login
                </button>
                <button 
                  onClick={() => navigate('/register')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full transition-colors duration-200 font-medium"
                >
                  Register
                </button>
              </div>
            )}

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button 
                onClick={toggleMobileMenu}
                className="text-gray-600 hover:text-blue-600 p-2"
                aria-label="Toggle menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {/* Home */}
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
            >
              Home
            </Link>

            {/* Shop with Categories */}
            <div className="px-3 py-2">
              <Link
                to="/shop"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-base font-medium text-gray-700 hover:text-blue-600 mb-2"
              >
                Shop
              </Link>
              {categories.length > 0 && (
                <div className="pl-4 space-y-1">
                  {categories.slice(0, 5).map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block py-1 text-sm text-gray-600 hover:text-blue-600"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* About */}
            <Link
              to="/about"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
            >
              About
            </Link>

            {/* Contact */}
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                setShowContactModal(true);
              }}
              className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
            >
              Contact
            </button>

            {/* AI Products - Available to all logged-in users */}
            {user && (
              <Link
                to="/ai-products"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-md text-base font-medium text-purple-600 hover:text-purple-700 hover:bg-purple-50"
              >
                🤖 AI Products
              </Link>
            )}

            {/* User Menu */}
            {user ? (
              <>
                <Link
                  to="/account"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                >
                  My Account
                </Link>
                {user.is_admin && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                  >
                    Admin Portal
                  </Link>
                )}
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 rounded-md text-base font-medium text-blue-600 hover:bg-blue-50 hover:bg-gray-50"
                >
                  Sign Up
                </Link>
              </>
            )}

            {/* Cart */}
            <Link
              to="/cart"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Cart ({cart?.length || 0})
            </Link>
          </div>
        </div>
      )}
      
      {/* Contact Form Modal */}
      <ContactFormModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} />
    </header>
  );
};

// Social Media Share Component - Updated to use configured social media links
const SocialShare = ({ url, title, description }) => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    // Fetch settings to get social media links
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/public`);
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to load settings for social share:', error);
      }
    };
    fetchSettings();
  }, []);

  const socialMediaLinks = settings?.company_settings?.social_media_links || {};

  return (
    <div className="bg-white py-8 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 text-center">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Share RG-3D with your friends!</h3>
        <div className="flex justify-center space-x-4">
          {/* Facebook */}
          {socialMediaLinks.facebook && (
            <a
              href={socialMediaLinks.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-white transition-colors duration-200 shadow-md hover:shadow-lg"
              title="Visit us on Facebook"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>
          )}

          {/* Twitter */}
          {socialMediaLinks.twitter && (
            <a
              href={socialMediaLinks.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-400 hover:bg-blue-500 text-white transition-colors duration-200 shadow-md hover:shadow-lg"
              title="Visit us on Twitter"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
              </svg>
            </a>
          )}

          {/* LinkedIn */}
          {socialMediaLinks.linkedin && (
            <a
              href={socialMediaLinks.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-700 hover:bg-blue-800 text-white transition-colors duration-200 shadow-md hover:shadow-lg"
              title="Visit us on LinkedIn"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          )}

          {/* Instagram */}
          {socialMediaLinks.instagram && (
            <a
              href={socialMediaLinks.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white transition-colors duration-200 shadow-md hover:shadow-lg"
              title="Visit us on Instagram"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.017 0C8.396 0 7.989.013 7.041.048 6.094.082 5.52.204 5.015.386c-.526.196-1.043.455-1.504.91-.46.455-.719.978-.91 1.504-.181.505-.304 1.079-.338 2.026C2.231 5.773 2.218 6.18 2.218 9.8v4.368c0 3.62.013 4.027.048 4.975.034.947.157 1.521.338 2.026.196.526.455 1.043.91 1.504.455.46.978.719 1.504.91.505.181 1.079.304 2.026.338.948.034 1.355.048 4.975.048h4.037c3.62 0 4.027-.013 4.975-.048.947-.034 1.521-.157 2.026-.338.526-.196 1.043-.455 1.504-.91.46-.455.719-.978.91-1.504.181-.505.304-1.079.338-2.026.034-.948.048-1.355.048-4.975V9.8c0-3.62-.013-4.027-.048-4.975-.034-.947-.157-1.521-.338-2.026a4.118 4.118 0 00-.91-1.504A4.118 4.118 0 0019.49.386C18.985.204 18.411.082 17.464.048 16.516.013 16.109 0 12.489 0h-.472zm-.52 2.169h.545c3.585 0 4.005.012 4.429.048.747.034 1.155.16 1.426.266.359.14.615.307.884.576.27.27.437.525.576.884.107.271.232.679.266 1.426.036.424.048.844.048 4.429v4.037c0 3.585-.012 4.005-.048 4.429-.034.747-.16 1.155-.266 1.426-.14.359-.307.615-.576.884a2.375 2.375 0 01-.884.576c-.271.107-.679.232-1.426.266-.424.036-.844.048-4.429.048h-4.037c-3.585 0-4.005-.012-4.429-.048-.747-.034-1.155-.16-1.426-.266a2.375 2.375 0 01-.884-.576 2.375 2.375 0 01-.576-.884c-.107-.271-.232-.679-.266-1.426-.036-.424-.048-.844-.048-4.429V9.8c0-3.585.012-4.005.048-4.429.034-.747.16-1.155.266-1.426.14-.359.307-.615.576-.884.27-.27.525-.437.884-.576.271-.107.679-.232 1.426-.266.424-.036.844-.048 4.429-.048zm.52 3.83a6 6 0 110 12 6 6 0 010-12zm0 2a4 4 0 100 8 4 4 0 000-8zm7.604-1.604a1.4 1.4 0 11-2.8 0 1.4 1.4 0 012.8 0z"/>
              </svg>
            </a>
          )}
        </div>
        <p className="text-sm text-gray-500 mt-4">
          Help us grow by sharing RG-3D's amazing 3D printed products with your network!
        </p>
      </div>
    </div>
  );
};

// Enhanced Home Page with Modern E-commerce Color Scheme
const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadFeaturedProducts();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/public`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      // Get products marked as featured
      const featured = response.data.filter(product => product.featured).slice(0, 3);
      if (featured.length === 0) {
        // If no featured products, use first 3 products
        setFeaturedProducts(response.data.slice(0, 3));
      } else {
        setFeaturedProducts(featured);
      }
    } catch (error) {
      console.error('Failed to load featured products:', error);
    }
  };

  const handleShopNow = () => {
    navigate('/products');
  };

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const handleViewAllProducts = () => {
    navigate('/products');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Hero Section with Integrated Logo */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="text-center space-y-8">
            {/* Company Logo as Central Focal Point */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                {settings?.company_settings?.logo_url && (
                  <img 
                    src={settings.company_settings.logo_url} 
                    alt="RG-3D Company Logo" 
                    className="w-48 h-48 md:w-64 md:h-64 object-contain mx-auto filter brightness-110 drop-shadow-lg"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-pulse"></div>
              </div>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white drop-shadow-lg">Welcome to RG - 3D</h1>
            <p className="text-2xl text-blue-100 mb-4 font-medium">Premium 3D Printed & Personalized Items</p>
            <p className="text-lg text-blue-200 mb-8 max-w-4xl mx-auto leading-relaxed">
              Discover unique, custom-made products crafted with precision using cutting-edge 3D printing technology. 
              From personalized gifts to functional items, we bring your ideas to life with professional quality and attention to detail.
            </p>
            <button 
              onClick={handleShopNow}
              className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Shop Now
            </button>
          </div>
        </div>
      </div>

      {/* Why Choose RG-3D Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Why Choose RG – 3D?</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Premium Quality */}
            <div className="text-center p-6 bg-blue-50 rounded-xl border border-blue-100 hover:border-blue-200 transition-colors duration-300 hover:shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Premium Quality</h3>
              <p className="text-gray-600 leading-relaxed">
                Every product is manufactured using industrial-grade 3D printers and premium materials, 
                ensuring durability, precision, and professional finishing that exceeds expectations.
              </p>
            </div>

            {/* Customization */}
            <div className="text-center p-6 bg-green-50 rounded-xl border border-green-100 hover:border-green-200 transition-colors duration-300 hover:shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 011-1h1a2 2 0 100-4H7a1 1 0 01-1-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Customization</h3>
              <p className="text-gray-600 leading-relaxed">
                Transform your ideas into reality with unlimited customization options. 
                Upload your designs, add personal text, choose colors, and create truly one-of-a-kind products.
              </p>
            </div>

            {/* Made to Order */}
            <div className="text-center p-6 bg-orange-50 rounded-xl border border-orange-100 hover:border-orange-200 transition-colors duration-300 hover:shadow-lg">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-3">Made to Order</h3>
              <p className="text-gray-600 leading-relaxed">
                Each item is crafted specifically for you when you place your order. 
                This ensures freshness, quality control, and the ability to accommodate your specific requirements.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Products Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Featured Products</h2>
            <p className="text-lg text-gray-600">Discover our most popular and innovative 3D printed creations</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {featuredProducts.map((product) => (
              <div 
                key={product.id} 
                className="bg-white rounded-xl shadow-md hover:shadow-xl overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-105 border border-gray-100"
                onClick={() => handleProductClick(product.id)}
              >
                <div className="bg-gray-100 aspect-square flex items-center justify-center">
                  {product.images && product.images.length > 0 ? (
                    <img 
                      src={getImageUrl(product.images[0])} 
                      alt={product.name} 
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentNode.innerHTML = '<span class="text-gray-500">No image available</span>';
                      }}
                    />
                  ) : (
                    <span className="text-gray-500">No image available</span>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">{product.name}</h3>
                  <div 
                    className="product-description-content product-description-preview text-gray-600 mb-4"
                    dangerouslySetInnerHTML={{ __html: product.description }}
                  />
                  <div className="flex justify-between items-center">
                    <span className="text-2xl font-bold text-blue-600">${product.price.toFixed(2)}</span>
                    <div className="flex space-x-1">
                      {Object.keys(product.base_options || {}).length > 0 && (
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">Customizable</span>
                      )}
                      {(product.addons || []).length > 0 && (
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">Add-ons</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* View All Products Link */}
          <div className="text-center">
            <button
              onClick={handleViewAllProducts}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full font-semibold text-lg transition-colors duration-300 shadow-md hover:shadow-lg"
            >
              View All Products
            </button>
          </div>
        </div>
      </div>

      {/* 3D Printing Excellence Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">3D Printing Excellence</h2>
            <p className="text-lg text-gray-600">Advanced materials and cutting-edge capabilities for superior results</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Materials Box */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-8 rounded-xl border border-blue-200 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Materials</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  <span className="text-gray-700 font-medium">PLA</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  <span className="text-gray-700 font-medium">PETG</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  <span className="text-gray-700 font-medium">Specialty filaments for unique applications</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  <span className="text-gray-700 font-medium">Wood</span>
                </div>
              </div>
            </div>

            {/* Capabilities Box */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-8 rounded-xl border border-green-200 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Capabilities</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-gray-700 font-medium">Layer resolution: 0.1mm - 0.3mm</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-gray-700 font-medium">Multi-color printing available</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-gray-700 font-medium">Custom sizing up to 350x350x350mm</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-gray-700 font-medium">Post-processing and finishing options</span>
                </div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                  <span className="text-gray-700 font-medium">Laser cutting & engraving</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Social Media Sharing */}
      <SocialShare 
        url={window.location.origin}
        title="RG-3D - Premium 3D Printed & Personalized Items"
        description="Discover unique, custom-made products crafted with precision using cutting-edge 3D printing technology."
      />
    </div>
  );
};

// Dedicated Create Account Page
const CreateAccountPage = () => {
  const { login } = useApp();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: '',
    acceptedTerms: false
  });
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [privacyContent, setPrivacyContent] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  // Fetch settings for logo
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/public`);
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    fetchSettings();
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      console.log('🔄 Fetching policies from:', `${API}/policies`);
      const response = await axios.get(`${API}/policies`);
      console.log('✅ Policies response:', response.data);
      
      // The response has structure: {success: true, data: [...]}
      const policies = response.data.data || response.data;
      console.log('📦 Policies array:', policies);
      
      // Find Terms & Conditions and Privacy Policy
      const terms = policies.find(p => p.slug === 'terms-and-conditions');
      const privacy = policies.find(p => p.slug === 'privacy-policy');
      
      console.log('📄 Terms found:', terms);
      console.log('📄 Privacy found:', privacy);
      
      if (terms) {
        console.log('✅ Setting terms content');
        setTermsContent(terms.content);
      } else {
        console.log('❌ Terms not found in policies');
      }
      
      if (privacy) {
        console.log('✅ Setting privacy content');
        setPrivacyContent(privacy.content);
      } else {
        console.log('❌ Privacy not found in policies');
      }
    } catch (error) {
      console.error('❌ Failed to load policies:', error);
      console.error('❌ Error details:', error.response?.data);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate T&C acceptance
      if (!formData.acceptedTerms) {
        toast.error('Please accept the Terms & Conditions to continue');
        setLoading(false);
        return;
      }

      // Registration logic
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }

      const response = await axios.post(`${API}/auth/register`, {
        name: formData.name,
        email: formData.email,
        password: formData.password
      });
      
      login(response.data.user, response.data.access_token);
      toast.success('Account created successfully!');
      navigate('/account/complete');  // Redirect to account completion
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGuestCheckout = async () => {
    try {
      const response = await axios.post(`${API}/auth/guest-session`);
      
      // Store guest token
      localStorage.setItem('guestToken', response.data.access_token);
      toast.success('Proceeding as guest. Orders will not be saved to an account.');
      navigate('/cart');
    } catch (error) {
      console.error('Guest session error:', error);
      toast.error('Failed to create guest session');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Logo and Header */}
          <div className="text-center mb-8">
            {settings?.company_settings?.logo_url && (
              <img 
                src={settings.company_settings.logo_url} 
                alt="RG-3D Logo" 
                className="w-16 h-16 mx-auto mb-4"
              />
            )}
            <h2 className="text-3xl font-bold text-gray-800">Create Account</h2>
            <p className="text-gray-600 mt-2">Join RG-3D for personalized 3D printing</p>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your full name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your email"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full px-3 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Create a strong password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  className="w-full px-3 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Confirm your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                  title={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Terms & Conditions Checkbox */}
            <div className="flex items-start">
              <input
                type="checkbox"
                id="acceptedTerms"
                checked={formData.acceptedTerms}
                onChange={(e) => setFormData({...formData, acceptedTerms: e.target.checked})}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                required
              />
              <label htmlFor="acceptedTerms" className="ml-2 block text-sm text-gray-700">
                I agree to the{' '}
                <button
                  type="button"
                  onClick={() => setShowTermsModal(true)}
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  Terms & Conditions
                </button>
                {' '}and{' '}
                <button
                  type="button"
                  onClick={() => setShowPrivacyModal(true)}
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  Privacy Policy
                </button>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors duration-200"
            >
              Already have an account? Sign in
            </button>
          </div>

          {/* Social Media Links */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-center text-sm text-gray-600 mb-4">Connect with us</p>
            <div className="flex justify-center space-x-4">
              {settings?.company_settings?.social_media_links?.facebook && (
                <a
                  href={settings.company_settings.social_media_links.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-blue-600 text-gray-600 hover:text-white flex items-center justify-center transition-all duration-300"
                  title="Facebook"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              )}
              {settings?.company_settings?.social_media_links?.twitter && (
                <a
                  href={settings.company_settings.social_media_links.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-blue-400 text-gray-600 hover:text-white flex items-center justify-center transition-all duration-300"
                  title="Twitter"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>
              )}
              {settings?.company_settings?.social_media_links?.linkedin && (
                <a
                  href={settings.company_settings.social_media_links.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-blue-700 text-gray-600 hover:text-white flex items-center justify-center transition-all duration-300"
                  title="LinkedIn"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              )}
              {settings?.company_settings?.social_media_links?.instagram && (
                <a
                  href={settings.company_settings.social_media_links.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white flex items-center justify-center transition-all duration-300"
                  title="Instagram"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.017 0C8.396 0 7.989.013 7.041.048 6.094.082 5.52.204 5.015.386c-.526.196-1.043.455-1.504.91-.46.455-.719.978-.91 1.504-.181.505-.304 1.079-.338 2.026C2.231 5.773 2.218 6.18 2.218 9.8v4.368c0 3.62.013 4.027.048 4.975.034.947.157 1.521.338 2.026.196.526.455 1.043.91 1.504.455.46.978.719 1.504.91.505.181 1.079.304 2.026.338.948.034 1.355.048 4.975.048h4.037c3.62 0 4.027-.013 4.975-.048.947-.034 1.521-.157 2.026-.338.526-.196 1.043-.455 1.504-.91.46-.455.719-.978.91-1.504.181-.505.304-1.079.338-2.026.034-.948.048-1.355.048-4.975V9.8c0-3.62-.013-4.027-.048-4.975-.034-.947-.157-1.521-.338-2.026a4.118 4.118 0 00-.91-1.504A4.118 4.118 0 0019.49.386C18.985.204 18.411.082 17.464.048 16.516.013 16.109 0 12.489 0h-.472zm-.52 2.169h.545c3.585 0 4.005.012 4.429.048.747.034 1.155.16 1.426.266.359.14.615.307.884.576.27.27.437.525.576.884.107.271.232.679.266 1.426.036.424.048.844.048 4.429v4.037c0 3.585-.012 4.005-.048 4.429-.034.747-.16 1.155-.266 1.426-.14.359-.307.615-.576.884a2.375 2.375 0 01-.884.576c-.271.107-.679.232-1.426.266-.424.036-.844.048-4.429.048h-4.037c-3.585 0-4.005-.012-4.429-.048-.747-.034-1.155-.16-1.426-.266a2.375 2.375 0 01-.884-.576 2.375 2.375 0 01-.576-.884c-.107-.271-.232-.679-.266-1.426-.036-.424-.048-.844-.048-4.429V9.8c0-3.585.012-4.005.048-4.429.034-.747.16-1.155.266-1.426.14-.359.307-.615.576-.884.27-.27.525-.437.884-.576.271-.107.679-.232 1.426-.266.424-.036.844-.048 4.429-.048zm.52 3.83a6 6 0 110 12 6 6 0 010-12zm0 2a4 4 0 100 8 4 4 0 000-8zm7.604-1.604a1.4 1.4 0 11-2.8 0 1.4 1.4 0 012.8 0z"/>
                  </svg>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Terms & Conditions Modal */}
        {showTermsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">Terms & Conditions</h3>
                <button
                  onClick={() => setShowTermsModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                {termsContent ? (
                  <div 
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: termsContent }}
                  />
                ) : (
                  <p className="text-gray-500">Loading terms...</p>
                )}
              </div>
              <div className="p-4 border-t border-gray-200">
                <button
                  onClick={() => setShowTermsModal(false)}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Privacy Policy Modal */}
        {showPrivacyModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">Privacy Policy</h3>
                <button
                  onClick={() => setShowPrivacyModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                {privacyContent ? (
                  <div 
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: privacyContent }}
                  />
                ) : (
                  <p className="text-gray-500">Loading privacy policy...</p>
                )}
              </div>
              <div className="p-4 border-t border-gray-200">
                <button
                  onClick={() => setShowPrivacyModal(false)}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Enhanced Login Page with Password Recovery and Security Features
const LoginPage = () => {
  const { login } = useApp();
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [settings, setSettings] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/public`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Login logic
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password
      });
      
      login(response.data.user, response.data.access_token);
      toast.success('Login successful!');
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || 'Login failed';
      
      // Handle failed login attempts
      setFailedAttempts(prev => prev + 1);
      
      // Show reset modal after 3 failed attempts
      if (failedAttempts + 1 >= 3) {
        setShowResetModal(true);
        setResetEmail(formData.email);
        toast.error('Multiple failed attempts detected. Use password reset to regain access.');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    try {
      setResettingPassword(true);
      
      const response = await axios.post(`${API}/auth/password-reset/request`, {
        email: resetEmail
      });
      
      if (response.data.success) {
        toast.success('Password reset instructions sent to your email address');
        setShowResetModal(false);
        setFailedAttempts(0);
      }
    } catch (error) {
      console.error('Password reset request failed:', error);
      toast.error('Failed to send password reset email');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleGuestCheckout = async () => {
    try {
      const response = await axios.post(`${API}/auth/guest-session`);
      
      // Store guest token
      localStorage.setItem('guestToken', response.data.access_token);
      toast.success('Proceeding as guest. Orders will not be saved to an account.');
      navigate('/cart');
    } catch (error) {
      console.error('Guest session error:', error);
      toast.error('Failed to create guest session');
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'} flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-md w-full">
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-8`}>
          {/* Logo and Header */}
          <div className="text-center mb-8">
            {settings?.company_settings?.logo_url && (
              <img 
                src={settings.company_settings.logo_url} 
                alt="RG-3D Logo" 
                className="w-16 h-16 mx-auto mb-4"
              />
            )}
            <h2 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Welcome Back</h2>
            <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mt-2`}>Sign in to your account</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Email Address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className={`w-full px-3 py-3 border ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                placeholder="Enter your email"
                required
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-1`}>Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className={`w-full px-3 py-3 pr-10 border ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute inset-y-0 right-0 flex items-center pr-3 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          {/* Reset Password Link */}
          <div className="mt-4 text-center">
            <button
              onClick={() => {
                setShowResetModal(true);
                setResetEmail(formData.email);
              }}
              className="text-blue-600 hover:text-blue-700 text-sm transition-colors duration-200"
            >
              Forgot your password?
            </button>
          </div>

          {/* Create Account Link */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/register')}
              className="text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors duration-200"
            >
              Don't have an account? Create one
            </button>
          </div>

          {/* Social Media Links */}
          <div className={`mt-8 pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <p className={`text-center text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-4`}>Connect with us</p>
            <div className="flex justify-center space-x-4">
              {settings?.company_settings?.social_media_links?.facebook && (
                <a
                  href={settings.company_settings.social_media_links.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isDark 
                      ? 'bg-gray-800 text-gray-400 hover:bg-blue-600 hover:text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-blue-600 hover:text-white'
                  }`}
                  title="Facebook"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              )}
              {settings?.company_settings?.social_media_links?.twitter && (
                <a
                  href={settings.company_settings.social_media_links.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isDark 
                      ? 'bg-gray-800 text-gray-400 hover:bg-blue-400 hover:text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-blue-400 hover:text-white'
                  }`}
                  title="Twitter"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>
              )}
              {settings?.company_settings?.social_media_links?.linkedin && (
                <a
                  href={settings.company_settings.social_media_links.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isDark 
                      ? 'bg-gray-800 text-gray-400 hover:bg-blue-700 hover:text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-blue-700 hover:text-white'
                  }`}
                  title="LinkedIn"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              )}
              {settings?.company_settings?.social_media_links?.instagram && (
                <a
                  href={settings.company_settings.social_media_links.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white flex items-center justify-center transition-all duration-300"
                  title="Instagram"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.017 0C8.396 0 7.989.013 7.041.048 6.094.082 5.52.204 5.015.386c-.526.196-1.043.455-1.504.91-.46.455-.719.978-.91 1.504-.181.505-.304 1.079-.338 2.026C2.231 5.773 2.218 6.18 2.218 9.8v4.368c0 3.62.013 4.027.048 4.975.034.947.157 1.521.338 2.026.196.526.455 1.043.91 1.504.455.46.978.719 1.504.91.505.181 1.079.304 2.026.338.948.034 1.355.048 4.975.048h4.037c3.62 0 4.027-.013 4.975-.048.947-.034 1.521-.157 2.026-.338.526-.196 1.043-.455 1.504-.91.46-.455.719-.978.91-1.504.181-.505.304-1.079.338-2.026.034-.948.048-1.355.048-4.975V9.8c0-3.62-.013-4.027-.048-4.975-.034-.947-.157-1.521-.338-2.026a4.118 4.118 0 00-.91-1.504A4.118 4.118 0 0019.49.386C18.985.204 18.411.082 17.464.048 16.516.013 16.109 0 12.489 0h-.472zm-.52 2.169h.545c3.585 0 4.005.012 4.429.048.747.034 1.155.16 1.426.266.359.14.615.307.884.576.27.27.437.525.576.884.107.271.232.679.266 1.426.036.424.048.844.048 4.429v4.037c0 3.585-.012 4.005-.048 4.429-.034.747-.16 1.155-.266 1.426-.14.359-.307.615-.576.884a2.375 2.375 0 01-.884.576c-.271.107-.679.232-1.426.266-.424.036-.844.048-4.429.048h-4.037c-3.585 0-4.005-.012-4.429-.048-.747-.034-1.155-.16-1.426-.266a2.375 2.375 0 01-.884-.576 2.375 2.375 0 01-.576-.884c-.107-.271-.232-.679-.266-1.426-.036-.424-.048-.844-.048-4.429V9.8c0-3.585.012-4.005.048-4.429.034-.747.16-1.155.266-1.426.14-.359.307-.615.576-.884.27-.27.525-.437.884-.576.271-.107.679-.232 1.426-.266.424-.036.844-.048 4.429-.048zm.52 3.83a6 6 0 110 12 6 6 0 010-12zm0 2a4 4 0 100 8 4 4 0 000-8zm7.604-1.604a1.4 1.4 0 11-2.8 0 1.4 1.4 0 012.8 0z"/>
                  </svg>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Password Reset Modal */}
        {showResetModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Reset Password</h3>
                  <button
                    onClick={() => setShowResetModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="mb-6">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.348 15.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span className="text-sm text-yellow-800">
                        Multiple failed login attempts detected. Reset your password to regain access.
                      </span>
                    </div>
                  </div>
                  
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your email address"
                    required
                  />
                </div>
                
                <div className="flex space-x-4">
                  <button
                    onClick={handlePasswordReset}
                    disabled={resettingPassword || !resetEmail}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {resettingPassword ? 'Sending...' : 'Send Reset Email'}
                  </button>
                  <button
                    onClick={() => setShowResetModal(false)}
                    className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
                
                <div className="mt-4 text-xs text-gray-500">
                  You'll receive an email with instructions to reset your password. The link will expire in 1 hour.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Simple Admin Login
const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { setUser } = useApp();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/admin/login`, { email, password });
      if (response.data.success) {
        setUser(response.data.user); // Update user state immediately
        localStorage.setItem('access_token', response.data.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
        toast.success('Admin login successful');
        navigate('/admin');
      }
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Admin Login
          </h2>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="admin@example.com"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter your password"
              />
            </div>
          </div>
          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Comprehensive Admin Portal
const AdminPortal = () => {
  const { user, loading } = useApp();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate('/admin/login');
        return;
      }
      if (!user?.is_admin) {
        navigate('/');
        return;
      }
      if (activeTab === 'dashboard') {
        loadAdminData();
      }
    }
  }, [user, loading, navigate, activeTab]);

  const loadAdminData = async () => {
    try {
      const [ordersRes, productsRes, customersRes] = await Promise.all([
        axios.get(`${API}/admin/orders`),
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/customers`)
      ]);
      setOrders(ordersRes.data);
      setProducts(productsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      toast.error('Failed to load admin data');
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
  }

  if (!user?.is_admin) {
    return null;
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'products':
        return <ProductsCatalogSection />;
      case 'orders':
        return <OrdersFulfillmentSection />;
      case 'customers':
        return <CustomersManagement />;
      case 'marketing':
        return <MarketingCommsSection />;
      case 'website':
        return <WebsiteSection />;
      case 'ai-products':
        return <AIProductsManager />;
      case 'settings':
        return <SettingsSection />;
      case 'file-manager':
        return <FileManager />;
      case 'dashboard':
        return <DashboardSection />;
      default:
        return <DashboardSection />;
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Admin Header with Notifications and Theme Toggle */}
        <div className="flex justify-between items-center mb-6">
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Admin Portal</h1>
          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${
                isDark 
                  ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700' 
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              } border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDark ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            <NotificationsDropdown isDark={isDark} />
          </div>
        </div>
        
        {/* Navigation Tabs */}
        <div className="mb-8">
          <nav className={`flex space-x-8 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            {[
              { id: 'dashboard', label: 'Dashboard', icon: '📊' },
              { id: 'products', label: 'Products & Catalog', icon: '📦' },
              { id: 'orders', label: 'Orders & Fulfillment', icon: '🛒' },
              { id: 'customers', label: 'Customers', icon: '👥' },
              { id: 'ai-products', label: 'AI Products', icon: '🤖' },
              { id: 'marketing', label: 'Marketing & Comms', icon: '📢' },
              { id: 'website', label: 'Website', icon: '🎨' },
              { id: 'file-manager', label: 'File Manager', icon: '📁' },
              { id: 'settings', label: 'Settings', icon: '⚙️' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : isDark 
                      ? 'border-transparent text-gray-400 hover:text-gray-200' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content with Theme Context */}
        <div className={isDark ? 'text-white' : 'text-gray-900'}>
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

// Products Page with Modern E-commerce Color Scheme
const ProductsPage = () => {
  const { addToCart } = useApp();
  const { isDark } = useTheme();
  const [products, setProducts] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to load products:', error);
      toast.error('Failed to load products');
    }
  };

  const handleViewProduct = (productId) => {
    navigate(`/product/${productId}`);
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'}`}>
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className={`text-5xl font-bold mb-4 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent`}>
            Our Products
          </h1>
          <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'} max-w-2xl mx-auto`}>
            Explore our collection of premium 3D printed products and personalized items
          </p>
        </div>

        {/* Enhanced Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {products.map((product) => (
            <EnhancedProductCard key={product.id} product={product} />
          ))}
        </div>

        {/* Empty State */}
        {products.length === 0 && (
          <div className="text-center py-20">
            <svg className={`w-24 h-24 mx-auto mb-4 ${isDark ? 'text-gray-700' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>No products available</h3>
            <p className={`${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Check back soon for new items!</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Enhanced Cart Page with Preview and Edit Functionality
const CartPage = () => {
  const { cart, updateCartQuantity, removeFromCart, getCartTotal, user } = useApp();
  const navigate = useNavigate();
  const [promoCode, setPromoCode] = useState('');
  const [discount, setDiscount] = useState(0);

  const handleEditItem = (item) => {
    navigate(`/product/${item.product_id}`);
  };

  const handleApplyPromo = async () => {
    if (!promoCode || promoCode.trim() === '') {
      toast.error('Please enter a promo code');
      return;
    }

    try {
      console.log('🔄 Validating promo code:', promoCode);
      const cartTotal = getCartTotal();
      
      const response = await axios.post(`${API}/discount-codes/validate`, {
        code: promoCode.toUpperCase(),
        order_total: cartTotal,
        customer_email: user?.email || '',
        cart_items: cart
      });

      console.log('✅ Promo code validation response:', response.data);
      
      if (response.data.valid) {
        setDiscount(response.data.discount_amount);
        toast.success(response.data.message || `${promoCode.toUpperCase()} applied!`);
      } else {
        toast.error('Invalid promo code');
      }
    } catch (error) {
      console.error('❌ Promo code validation error:', error);
      if (error.response?.status === 404) {
        toast.error('Invalid promo code');
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to validate promo code');
      }
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-4 py-8 text-center">
          <h1 className="text-3xl font-bold mb-4 text-gray-800">Your Cart</h1>
          <div className="bg-white rounded-lg shadow-md p-8 max-w-md mx-auto">
            <div className="text-6xl mb-4">🛒</div>
            <p className="text-gray-600 mb-8">Your cart is empty</p>
            <button
              onClick={() => navigate('/products')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  const subtotal = getCartTotal();
  const discountedSubtotal = subtotal - discount;
  const tax = discountedSubtotal * 0.13;
  const total = discountedSubtotal + tax;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Your Cart</h1>
      
      <div className="space-y-4">
        {cart.map((item, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start">
              <div className="flex space-x-4">
                {/* Product Image with Preview */}
                <div className="w-20 h-20 bg-gray-100 rounded overflow-hidden">
                  {item.product?.images && item.product.images.length > 0 ? (
                    <ImageThumbnail
                      imageUrl={getImageUrl(item.product.images[0])}
                      altText={item.product.name}
                      title={`${item.product.name} - Product Image`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                      No image
                    </div>
                  )}
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold">
                    {item.name}
                    {item.is_included && (
                      <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                        Included
                      </span>
                    )}
                  </h3>
                  <p className="text-gray-600">
                    {item.is_included ? (
                      <span className="text-green-600 font-medium">No additional charge</span>
                    ) : (
                      `$${item.price.toFixed(2)} each`
                    )}
                  </p>
                  {item.description && (
                    <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                  )}
                  
                  {/* Show product configuration */}
                  {item.selected_options && Object.keys(item.selected_options).length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-600">Options:</p>
                      {Object.entries(item.selected_options).map(([key, value]) => (
                        <span key={key} className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs mr-1">
                          {key}: {value}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {item.custom_text_inputs && Object.keys(item.custom_text_inputs).length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-600">Customization:</p>
                      {Object.entries(item.custom_text_inputs).map(([key, value]) => (
                        <div key={key} className="text-sm">
                          <strong>{key}:</strong> "{value}"
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {!item.is_included && item.product && (
                    <button
                      onClick={() => handleEditItem(item)}
                      className="text-blue-600 hover:text-blue-700 text-sm mt-2"
                    >
                      ✏️ Edit Options
                    </button>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                {!item.is_included && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => updateCartQuantity(index, item.quantity - 1)}
                      className="w-8 h-8 rounded bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                      disabled={item.quantity <= 1}
                    >
                      -
                    </button>
                    <span className="w-8 text-center">{item.quantity}</span>
                    <button
                      onClick={() => updateCartQuantity(index, item.quantity + 1)}
                      className="w-8 h-8 rounded bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                    >
                      +
                    </button>
                  </div>
                )}
                {item.is_included && (
                  <span className="text-sm text-gray-500">Qty: {item.quantity}</span>
                )}
                <button
                  onClick={() => removeFromCart(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        {/* Promo Code */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Promo Code</label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              placeholder="SAVE10, WELCOME20"
              className="flex-1 px-3 py-2 border border-gray-300 rounded"
            />
            <button
              onClick={handleApplyPromo}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Apply
            </button>
          </div>
        </div>

        {/* Order Summary */}
        <div className="space-y-2 mb-4">
          <div className="flex justify-between">
            <span>Items ({cart.length}):</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-green-600">
              <span>Discount ({promoCode}):</span>
              <span>-${discount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span>Tax (estimated):</span>
            <span>${tax.toFixed(2)}</span>
          </div>
          <hr />
          <div className="flex justify-between font-semibold text-lg">
            <span>Total:</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>
        
        <button
          onClick={() => navigate('/checkout')}
          className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700"
        >
          Proceed to Checkout - ${total.toFixed(2)}
        </button>
      </div>
    </div>
  );
};

// Enhanced Checkout Page with Authentication and Standard Features
const CheckoutPage = () => {
  const { cart, getCartTotal, clearCart, user } = useApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState('shipping');
  const [paymentMethod, setPaymentMethod] = useState('etransfer');
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [shippingCost, setShippingCost] = useState(0);
  const [publicSettings, setPublicSettings] = useState(null);
  const [showAuthOptions, setShowAuthOptions] = useState(!user);
  const [checkoutAsGuest, setCheckoutAsGuest] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [saveAddress, setSaveAddress] = useState(false);
  const [newsletterSignup, setNewsletterSignup] = useState(true); // Default to opt-in for marketing emails
  const [countries] = useState([
    { code: 'CA', name: 'Canada' },
    { code: 'US', name: 'United States' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'AU', name: 'Australia' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'JP', name: 'Japan' },
    { code: 'NL', name: 'Netherlands' }
  ]);
  const [customerInfo, setCustomerInfo] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: '',
    city: '',
    province: '',
    postal: '',
    country: 'CA'
  });

  const loadPublicSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/public`);
      setPublicSettings(response.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  useEffect(() => {
    loadPublicSettings();
    // Load complete user profile with addresses for logged-in users
    if (user) {
      loadUserProfile();
    }
  }, [user]);

  const loadUserProfile = async () => {
    try {
      console.log('Loading user profile for checkout...');
      const response = await axios.get(`${API}/customers/profile`);
      const userProfile = response.data;
      console.log('User profile loaded:', userProfile);
      
      // Use shipping address if available, otherwise use billing address
      const addressToUse = userProfile.shipping_address || userProfile.billing_address;
      console.log('Address to use:', addressToUse);
      
      if (addressToUse) {
        const newCustomerInfo = {
          ...customerInfo,
          name: userProfile.name || customerInfo.name,
          email: userProfile.email || customerInfo.email,
          phone: userProfile.phone || customerInfo.phone,
          address: addressToUse.street || '',
          city: addressToUse.city || '',
          province: addressToUse.province || '',
          postal: addressToUse.postal_code || '',
          country: addressToUse.country || 'CA'
        };
        
        console.log('Setting customer info:', newCustomerInfo);
        setCustomerInfo(newCustomerInfo);
        
        // Trigger shipping rate calculation after address is populated
        if (deliveryMethod === 'shipping' && addressToUse.postal_code && addressToUse.city) {
          console.log('Triggering shipping rate calculation...');
          // Small delay to ensure state is updated before shipping calculation
          setTimeout(() => {
            getShippingRates();
          }, 100);
        }
      } else {
        console.log('No saved address found in user profile');
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
      console.log('Error details:', error.response?.data);
      // Continue with checkout even if profile loading fails
    }
  };

  useEffect(() => {
    if (deliveryMethod === 'shipping' && customerInfo.postal && customerInfo.city) {
      getShippingRates();
    }
  }, [deliveryMethod, customerInfo.postal, customerInfo.city, customerInfo.province]);

  // Additional trigger for when user profile loads with address
  useEffect(() => {
    if (user && deliveryMethod === 'shipping' && customerInfo.postal && customerInfo.city && customerInfo.province) {
      // Delay to ensure the address auto-population has completed
      const timer = setTimeout(() => {
        getShippingRates();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [user, customerInfo.postal, customerInfo.city, customerInfo.province]);

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  const getShippingRates = async () => {
    if (!customerInfo.postal || !customerInfo.city) return;

    setLoadingShipping(true);
    try {
      const shippingMethod = publicSettings?.shipping_settings?.shipping_method || 'canada_post';
      const canadaPostEnabled = publicSettings?.shipping_settings?.canada_post_enabled;
      
      let response;
      
      if (shippingMethod === 'flat_rate') {
        // Use flat rate shipping calculation
        response = await axios.post(`${API}/shipping/flat-rates/calculate`, {
          cart_items: cart.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity
          }))
        });
      } else {
        // Use Canada Post shipping calculation (default)
        response = await axios.post(`${API}/shipping/rates`, {
          destination: {
            postal_code: customerInfo.postal,
            city: customerInfo.city,
            province: customerInfo.province || 'ON'
          },
          cart_items: cart.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity
          }))
        });
      }

      if (response.data.rates && response.data.rates.length > 0) {
        setShippingOptions(response.data.rates);
        // Auto-select cheapest option
        const cheapest = response.data.rates.reduce((prev, current) => 
          prev.price < current.price ? prev : current
        );
        setSelectedShipping(cheapest);
        setShippingCost(cheapest.price);
      }
    } catch (error) {
      console.error('Failed to get shipping rates:', error);
      // Provide fallback shipping options
      const fallbackRates = [
        { service_code: 'DOM.RP', service_name: 'Regular Parcel', price: 15.00, delivery_time: '2-8 business days' },
        { service_code: 'DOM.EP', service_name: 'Expedited Parcel', price: 25.00, delivery_time: '1-2 business days' },
        { service_code: 'DOM.XP', service_name: 'Xpresspost', price: 30.00, delivery_time: '1-2 business days' }
      ];
      setShippingOptions(fallbackRates);
      setSelectedShipping(fallbackRates[0]);
      setShippingCost(fallbackRates[0].price);
    } finally {
      setLoadingShipping(false);
    }
  };

  const handleShippingChange = (option) => {
    setSelectedShipping(option);
    setShippingCost(option.price);
  };

  const handleGuestCheckout = () => {
    setCheckoutAsGuest(true);
    setShowAuthOptions(false);
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // For e-transfer, place order directly
      if (paymentMethod === 'etransfer') {
        const orderData = {
          customer_info: {
            ...customerInfo,
            newsletter_signup: newsletterSignup,
            save_address: saveAddress && user ? true : false
          },
          items: cart.map(item => ({
            product_id: item.product_id,
            product_name: item.product?.name || item.product_name,
            quantity: item.quantity,
            price: item.price,
            selected_options: item.selected_options || {},
            custom_text_inputs: item.custom_text_inputs || {},
            selected_addons: item.selected_addons || [],
            uploaded_files: item.uploaded_files || [],
            addon_total: item.addon_total || 0,
            option_total: item.option_total || 0
          })),
          payment_method: paymentMethod,
          delivery_method: deliveryMethod,
          shipping_service: deliveryMethod === 'shipping' ? selectedShipping?.service_code : null,
          shipping_cost: deliveryMethod === 'shipping' ? shippingCost : 0,
          pickup_location: deliveryMethod === 'pickup' ? 'Cambridge, Ontario, Canada' : null,
          promo_code: promoCode,
          guest_checkout: !user || checkoutAsGuest
        };

        const response = await axios.post(`${API}/orders`, orderData);
        
        if (response.data) {
          toast.success('Order placed successfully! Check your email for e-transfer instructions.');
          clearCart();
          navigate('/');
        }
      } else {
        // For PayPal, we'll handle it differently with PayPal buttons (see below)
        toast('Please use the PayPal button below to complete your payment.', {
          icon: 'ℹ️',
          duration: 4000,
        });
      }
    } catch (error) {
      console.error('Order failed:', error);
      toast.error('Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalSuccess = async (details, data) => {
    try {
      setLoading(true);
      
      // Create order with PayPal payment details
      const orderData = {
        customer_info: {
          ...customerInfo,
          newsletter_signup: newsletterSignup,
          save_address: saveAddress && user ? true : false
        },
        items: cart.map(item => ({
          product_id: item.product_id,
          product_name: item.product?.name || item.product_name,
          quantity: item.quantity,
          price: item.price,
          selected_options: item.selected_options || {},
          custom_text_inputs: item.custom_text_inputs || {},
          selected_addons: item.selected_addons || [],
          uploaded_files: item.uploaded_files || [],
          addon_total: item.addon_total || 0,
          option_total: item.option_total || 0
        })),
        payment_method: 'paypal',
        delivery_method: deliveryMethod,
        shipping_service: deliveryMethod === 'shipping' ? selectedShipping?.service_code : null,
        shipping_cost: deliveryMethod === 'shipping' ? shippingCost : 0,
        pickup_location: deliveryMethod === 'pickup' ? 'Cambridge, Ontario, Canada' : null,
        promo_code: promoCode,
        guest_checkout: !user || checkoutAsGuest,
        payment_details: {
          paypal_order_id: data.orderID,
          paypal_payment_id: details.id,
          payer_email: details.payer?.email_address
        }
      };

      const response = await axios.post(`${API}/orders`, orderData);
      
      if (response.data) {
        toast.success('PayPal payment successful! Order placed.');
        clearCart();
        navigate('/');
      }
    } catch (error) {
      console.error('PayPal order creation failed:', error);
      toast.error('Failed to complete order after PayPal payment');
    } finally {
      setLoading(false);
    }
  };

  const subtotal = getCartTotal();
  const shipping = deliveryMethod === 'shipping' ? shippingCost : 0;
  const tax = (subtotal + shipping) * 0.13;
  const total = subtotal + shipping + tax;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8 text-gray-800">Checkout</h1>

        {/* Authentication Options for Non-Logged-In Users */}
        {!user && showAuthOptions && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">How would you like to checkout?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-lg p-4 hover:border-blue-500 transition-colors">
                <h3 className="font-semibold text-lg mb-2">Sign In to Your Account</h3>
                <p className="text-gray-600 mb-4">Access your saved addresses and view order history</p>
                <ul className="text-sm text-gray-600 mb-4 space-y-1">
                  <li>• Faster checkout with saved information</li>
                  <li>• Order history and tracking</li>
                  <li>• Account management</li>
                </ul>
                <button
                  onClick={handleSignIn}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  Sign In
                </button>
              </div>
              <div className="border rounded-lg p-4 hover:border-green-500 transition-colors">
                <h3 className="font-semibold text-lg mb-2">Continue as Guest</h3>
                <p className="text-gray-600 mb-4">Quick checkout without creating an account</p>
                <ul className="text-sm text-gray-600 mb-4 space-y-1">
                  <li>• No account creation required</li>
                  <li>• Fast one-time checkout</li>
                  <li>• Still receive order confirmation</li>
                </ul>
                <button
                  onClick={handleGuestCheckout}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  Checkout as Guest
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Show checkout form only if user is logged in or chose guest checkout */}
        {(user || checkoutAsGuest) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Customer Information */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-800">Customer Information</h2>
                    {checkoutAsGuest && (
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                        Guest Checkout
                      </span>
                    )}
                  </div>
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Full Name"
                      value={customerInfo.name}
                      onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={customerInfo.email}
                      onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <input
                      type="tel"
                      placeholder="Phone Number (Optional)"
                      value={customerInfo.phone}
                      onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Delivery Method */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-xl font-semibold mb-4 text-gray-800">Delivery Method</h2>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        id="shipping"
                        name="delivery"
                        value="shipping"
                        checked={deliveryMethod === 'shipping'}
                        onChange={(e) => setDeliveryMethod(e.target.value)}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="shipping" className="text-gray-700 font-medium">
                        📦 Optional Shipping
                        {publicSettings?.shipping_settings?.shipping_method === 'canada_post' && publicSettings?.shipping_settings?.canada_post_enabled ? (
                          <span className="text-blue-600"> Through Canada Post</span>
                        ) : publicSettings?.shipping_settings?.shipping_method === 'flat_rate' ? (
                          <span className="text-purple-600"> Through Flat Rate Prepaid Box</span>
                        ) : null}
                      </label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        id="pickup"
                        name="delivery"
                        value="pickup"
                        checked={deliveryMethod === 'pickup'}
                        onChange={(e) => setDeliveryMethod(e.target.value)}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="pickup" className="text-gray-700 font-medium">
                        🏪 Local Pickup - Cambridge, Ontario, Canada
                      </label>
                    </div>
                  </div>
                </div>

                {/* Shipping Address (only if shipping selected) */}
                {deliveryMethod === 'shipping' && (
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h2 className="text-xl font-semibold mb-4 text-gray-800">Shipping Address</h2>
                    <div className="space-y-4">
                      <input
                        type="text"
                        placeholder="Street Address"
                        value={customerInfo.address}
                        onChange={(e) => setCustomerInfo({...customerInfo, address: e.target.value})}
                        className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="City"
                          value={customerInfo.city}
                          onChange={(e) => setCustomerInfo({...customerInfo, city: e.target.value})}
                          className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <input
                          type="text"
                          placeholder="Province/State"
                          value={customerInfo.province}
                          onChange={(e) => setCustomerInfo({...customerInfo, province: e.target.value})}
                          className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Postal Code"
                          value={customerInfo.postal}
                          onChange={(e) => setCustomerInfo({...customerInfo, postal: e.target.value.toUpperCase()})}
                          className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-700">Country</label>
                          <select
                            value={customerInfo.country}
                            onChange={(e) => setCustomerInfo({...customerInfo, country: e.target.value})}
                            className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                          >
                            {countries.map(country => (
                              <option key={country.code} value={country.code}>
                                {country.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Save Address Option for Logged-in Users */}
                      {user && (
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="saveAddress"
                            checked={saveAddress}
                            onChange={(e) => setSaveAddress(e.target.checked)}
                            className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                          />
                          <label htmlFor="saveAddress" className="text-sm text-gray-600">
                            Save this address to my account for future orders
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Shipping Options (only if shipping selected) */}
                {deliveryMethod === 'shipping' && (
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h2 className="text-xl font-semibold mb-4 text-gray-800">Shipping Options</h2>
                    {loadingShipping ? (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="text-gray-600 mt-2">Getting shipping rates...</p>
                      </div>
                    ) : shippingOptions.length > 0 ? (
                      <div className="space-y-3">
                        {shippingOptions.map((option, index) => (
                          <div
                            key={index}
                            className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                              selectedShipping?.service_code === option.service_code
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => handleShippingChange(option)}
                          >
                            <div className="flex items-center space-x-3">
                              <input
                                type="radio"
                                checked={selectedShipping?.service_code === option.service_code}
                                onChange={() => handleShippingChange(option)}
                                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-medium text-gray-800">{option.service_name}</p>
                                    <p className="text-sm text-gray-600">{option.delivery_time}</p>
                                    {option.fuel_surcharge > 0 && (
                                      <p className="text-xs text-orange-600">
                                        Fuel Surcharge: +${option.fuel_surcharge.toFixed(2)} ({option.fuel_surcharge_percent}%)
                                      </p>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <span className="font-bold text-blue-600">${option.price.toFixed(2)}</span>
                                    {option.base_price && option.base_price !== option.price && (
                                      <div className="text-xs text-gray-500">
                                        Base: ${option.base_price.toFixed(2)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : customerInfo.postal && customerInfo.city ? (
                      <p className="text-gray-600">Enter postal code and city to see shipping options</p>
                    ) : (
                      <p className="text-gray-600">Please enter your address to calculate shipping</p>
                    )}
                  </div>
                )}

                {/* Payment Method Selection */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-xl font-semibold mb-4 text-gray-800">Payment Method</h2>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        id="etransfer"
                        name="payment"
                        value="etransfer"
                        checked={paymentMethod === 'etransfer'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="etransfer" className="text-gray-700 font-medium">
                        💳 Interac E-Transfer (Recommended)
                      </label>
                    </div>
                    
                    {/* Only show PayPal option if properly configured */}
                    {publicSettings?.paypal_enabled && publicSettings?.paypal_client_id && publicSettings?.paypal_business_email && (
                      <div className="flex items-center space-x-3">
                        <input
                          type="radio"
                          id="paypal"
                          name="payment"
                          value="paypal"
                          checked={paymentMethod === 'paypal'}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="paypal" className="text-gray-700 font-medium">
                          💰 PayPal
                        </label>
                      </div>
                    )}
                    
                    {/* PayPal Unavailable Notice */}
                    {(!publicSettings?.paypal_enabled || !publicSettings?.paypal_client_id || !publicSettings?.paypal_business_email) && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-600 text-center">
                          💳 PayPal payments will be available once configured by admin.
                        </p>
                      </div>
                    )}
                    
                    {/* E-Transfer Instructions */}
                    {paymentMethod === 'etransfer' && (
                      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <h3 className="font-semibold text-blue-800 mb-2">📧 E-Transfer Instructions</h3>
                        <div className="text-sm text-blue-700 space-y-2">
                          <p><strong>Send E-Transfer to:</strong> <code className="bg-blue-100 px-2 py-1 rounded">timelrd@rogers.com</code></p>
                          <p><strong>Auto-Deposit:</strong> ✅ This account is setup for auto-deposit</p>
                          <div className="mt-3 p-3 bg-blue-100 rounded border-l-4 border-blue-400">
                            <p className="font-medium text-blue-800">📝 Important:</p>
                            <p className="text-blue-700">Please include your <strong>order number</strong> in the e-transfer message for faster processing. You will receive your order number via email after placing this order.</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* PayPal Instructions */}
                    {paymentMethod === 'paypal' && (
                      <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                        <h3 className="font-semibold text-yellow-800 mb-2">💳 PayPal Payment</h3>
                        <p className="text-sm text-yellow-700">
                          You will be redirected to PayPal to complete your payment after placing the order.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Checkout Options */}
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-xl font-semibold mb-4 text-gray-800">Additional Options</h2>
                  <div className="space-y-4">
                    {/* Promo Code */}
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-700">Promo Code (Optional)</label>
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          placeholder="Enter promo code"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                          onClick={() => toast('Promo code functionality coming soon!', {
                            icon: 'ℹ️',
                            duration: 3000,
                          })}
                        >
                          Apply
                        </button>
                      </div>
                    </div>

                    {/* Newsletter Signup */}
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="newsletter"
                        checked={newsletterSignup}
                        onChange={(e) => setNewsletterSignup(e.target.checked)}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="newsletter" className="text-sm text-gray-600">
                        📬 Subscribe to our newsletter for updates and special offers
                      </label>
                    </div>

                  </div>
                </div>

                {/* Submit Button for E-Transfer */}
                {paymentMethod === 'etransfer' && (
                  <button
                    type="submit"
                    disabled={loading || (deliveryMethod === 'shipping' && !selectedShipping)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Placing Order...' : `Place Order - $${total.toFixed(2)}`}
                  </button>
                )}
              </form>

              {/* PayPal Buttons Component */}
              {paymentMethod === 'paypal' && publicSettings?.paypal_enabled && publicSettings?.paypal_client_id && (
                <div className="w-full mt-6">
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-700 text-center">
                      Complete your information above, then use the PayPal button below to pay securely.
                    </p>
                  </div>
                  
                  {/* Validate before showing PayPal buttons */}
                  {(!customerInfo.name || !customerInfo.email || (deliveryMethod === 'shipping' && (!customerInfo.address || !customerInfo.city || !customerInfo.postal || !selectedShipping))) ? (
                    <div className="w-full p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                      <p className="text-yellow-700 font-medium">Please complete all required fields above</p>
                    </div>
                  ) : (
                    <PayPalCheckout
                      customerInfo={customerInfo}
                      cart={cart}
                      total={total}
                      deliveryMethod={deliveryMethod}
                      selectedShipping={selectedShipping}
                      shippingCost={shippingCost}
                      newsletterSignup={newsletterSignup}
                      saveAddress={saveAddress}
                      user={user}
                      checkoutAsGuest={checkoutAsGuest}
                      promoCode={promoCode}
                      onSuccess={(details) => {
                        // Show success modal or redirect
                        toast.success(`Order ${details.order_number} confirmed!`);
                        setTimeout(() => {
                          navigate('/customer/account');
                        }, 2000);
                      }}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Order Summary */}
            <div className="bg-white rounded-xl shadow-md p-6 h-fit">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">Order Summary</h2>
              <div className="space-y-4">
                {cart.map((item, index) => (
                  <div key={index} className="flex justify-between items-start">
                    <div className="flex-1">
                      <span className="font-medium">{item.product.name}</span>
                      {item.is_included && (
                        <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          Included
                        </span>
                      )}
                      <span className="text-gray-600"> (x{item.quantity})</span>
                      {item.selected_options && Object.keys(item.selected_options).length > 0 && (
                        <div className="text-sm text-gray-500 mt-1">
                          {Object.entries(item.selected_options).map(([key, value]) => (
                            <div key={key}>{key}: {value}</div>
                          ))}
                        </div>
                      )}
                      {item.selected_addons && item.selected_addons.length > 0 && (
                        <div className="text-sm mt-1 space-y-0.5">
                          {item.selected_addons.map((addonId, addonIdx) => {
                            const addon = item.product?.addons?.find(a => a.id === addonId);
                            if (!addon) return null;
                            return (
                              <div key={addonIdx} className="text-blue-600">
                                + {addon.name || addon.custom_name || 'Add-on'}
                                {addon.price > 0 && (
                                  <span className="text-gray-600 ml-1">(+${addon.price.toFixed(2)})</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <span className="font-medium">
                      {item.is_included ? (
                        <span className="text-green-600">Included</span>
                      ) : (
                        `$${(item.price * item.quantity).toFixed(2)}`
                      )}
                    </span>
                  </div>
                ))}
                <hr className="border-gray-200" />
                
                {/* Promo Code Discount */}
                {promoCode && (
                  <div className="flex justify-between text-green-600">
                    <span>Promo Code ({promoCode}):</span>
                    <span>-$0.00</span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                {shipping > 0 && (
                  <div className="flex justify-between">
                    <span>Shipping ({selectedShipping?.service_name}):</span>
                    <span>${shipping.toFixed(2)}</span>
                  </div>
                )}
                {deliveryMethod === 'pickup' && (
                  <div className="text-sm text-green-600">
                    ✅ Free pickup in Cambridge, Ontario
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Tax (13% HST):</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
                <hr className="border-gray-200" />
                <div className="flex justify-between font-semibold text-lg">
                  <span>Total:</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>

              {/* Order Security & Trust */}
              <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center space-x-2 mb-2">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.586-4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2h-5.586l-2-2H8l-2 2z" />
                  </svg>
                  <span className="font-semibold text-green-800">Secure Checkout</span>
                </div>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• SSL encrypted transaction</li>
                  <li>• Order confirmation via email</li>
                  <li>• Canadian business since 2025</li>
                  <li>• Professional 3D printing services</li>
                </ul>
              </div>

              {/* Payment Method Info */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-800 mb-2">
                  Payment Method: {paymentMethod === 'etransfer' ? 'Interac E-Transfer' : 'PayPal'}
                </h3>
                <p className="text-sm text-blue-700">
                  {paymentMethod === 'etransfer' 
                    ? 'Send e-transfer to timelrd@rogers.com with your order number after placing order.'
                    : 'You will be redirected to PayPal to complete payment after placing order.'
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Social Media Sharing */}
        <SocialShare 
          url={window.location.origin}
          title="RG-3D - Premium 3D Printed & Personalized Items"
          description="Just discovered amazing 3D printed products at RG-3D! Check out their custom designs and personalization options."
        />
      </div>
    </div>
  );
};

// Enhanced Product Detail Page with Integrated Color Scheme
const ProductDetailPage = () => {
  const { productId } = useParams();
  const { addToCart } = useApp();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [customTextInputs, setCustomTextInputs] = useState({});
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [prepaidShippingCost, setPrepaidShippingCost] = useState(0);

  useEffect(() => {
    loadProduct();
    loadCategories();
  }, [productId]);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
      
      // Initialize selected options with first option for each category
      const initialOptions = {};
      Object.entries(response.data.base_options || {}).forEach(([optionName, options]) => {
        if (options.length > 0) {
          initialOptions[optionName] = options[0].name;
        }
      });
      setSelectedOptions(initialOptions);
    } catch (error) {
      console.error('Failed to load product:', error);
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    // Validate max files
    const maxFiles = product.max_files_allowed || 10;
    if (uploadedFiles.length >= maxFiles) {
      toast.error(`Maximum ${maxFiles} file${maxFiles > 1 ? 's' : ''} allowed. Please remove a file before uploading another.`);
      return;
    }

    // Validate file size
    const maxSizeMB = product.max_file_size_mb || 10;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      toast.error(`File size exceeds ${maxSizeMB}MB limit. Please choose a smaller file.`);
      return;
    }

    // Validate file type
    const allowedTypes = product.allowed_file_types || 'image/*,.pdf,.stl,.obj,.zip,.rar';
    const isValidType = allowedTypes.split(',').some(type => {
      const trimmedType = type.trim();
      if (trimmedType === '*') return true;
      if (trimmedType.startsWith('.')) {
        return file.name.toLowerCase().endsWith(trimmedType.toLowerCase());
      }
      if (trimmedType.includes('/*')) {
        const category = trimmedType.split('/')[0];
        return file.type.startsWith(category + '/');
      }
      return file.type === trimmedType;
    });

    if (!isValidType) {
      toast.error(`File type not allowed. Accepted types: ${allowedTypes}`);
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadedFiles([...uploadedFiles, response.data]);
      toast.success(`File uploaded successfully (${uploadedFiles.length + 1}/${maxFiles})`);
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const calculatePrice = () => {
    if (!product) return 0;
    
    let totalPrice = product.price;
    
    // Add option prices
    Object.entries(selectedOptions).forEach(([optionName, selectedValue]) => {
      const options = product.base_options[optionName] || [];
      const selectedOption = options.find(opt => opt.name === selectedValue);
      if (selectedOption) {
        totalPrice += selectedOption.price;
      }
    });
    
    // Add custom text option prices
    Object.entries(customTextInputs).forEach(([fieldName, value]) => {
      if (value && product.custom_options[fieldName]) {
        const customOption = product.custom_options[fieldName];
        if (customOption.price) {
          totalPrice += customOption.price;
        }
      }
    });
    
    // Add addon prices
    selectedAddons.forEach(addonId => {
      const addon = product.addons.find(a => a.id === addonId);
      if (addon) {
        totalPrice += addon.price;
      }
    });
    
    // Add prepaid shipping cost
    totalPrice += prepaidShippingCost;
    
    return totalPrice;
  };

  const loadPrepaidShippingCost = async () => {
    if (product?.prepaid_shipping_enabled && product?.selected_flat_rate_id) {
      try {
        const flatRatesResponse = await axios.get(`${API}/shipping/flat-rates`);
        const flatRates = flatRatesResponse.data;
        const selectedFlatRate = flatRates.find(rate => rate.id === product.selected_flat_rate_id);
        if (selectedFlatRate) {
          setPrepaidShippingCost(selectedFlatRate.price);
        }
      } catch (error) {
        console.error('Failed to load prepaid shipping cost:', error);
      }
    } else {
      setPrepaidShippingCost(0);
    }
  };

  useEffect(() => {
    if (product) {
      loadPrepaidShippingCost();
    }
  }, [product]);

  const handleAddToCart = () => {
    // Validate file upload requirement
    if (product.file_upload_required && uploadedFiles.length === 0) {
      toast.error('Please upload at least one file before adding to cart. This product requires file upload.');
      // Scroll to file upload section
      const fileUploadSection = document.querySelector('[data-file-upload-section]');
      if (fileUploadSection) {
        fileUploadSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // Validate max files
    if (product.max_files_allowed && uploadedFiles.length > product.max_files_allowed) {
      toast.error(`Maximum ${product.max_files_allowed} files allowed. Please remove ${uploadedFiles.length - product.max_files_allowed} file(s).`);
      const fileUploadSection = document.querySelector('[data-file-upload-section]');
      if (fileUploadSection) {
        fileUploadSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const cartItem = {
      product_id: product.id,
      quantity: 1,
      selected_options: selectedOptions,
      selected_option_prices: {},
      custom_text_inputs: customTextInputs,
      selected_addons: selectedAddons,
      uploaded_files: uploadedFiles,
      price: calculatePrice(),
      addon_total: selectedAddons.reduce((total, addonId) => {
        const addon = product.addons.find(a => a.id === addonId);
        return total + (addon ? addon.price : 0);
      }, 0),
      option_total: Object.entries(selectedOptions).reduce((total, [optionName, selectedValue]) => {
        const options = product.base_options[optionName] || [];
        const selectedOption = options.find(opt => opt.name === selectedValue);
        return total + (selectedOption ? selectedOption.price : 0);
      }, 0)
    };
    
    addToCart({ ...cartItem, product });
    toast.success('Added to cart!');
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'} flex justify-center items-center`}>
        <div className={isDark ? 'text-gray-300' : 'text-gray-600'}>Loading product...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'}`}>
        <div className={`text-center py-8 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Product not found</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'}`}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Breadcrumb Navigation */}
        <nav className={`flex items-center space-x-2 text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} aria-label="Breadcrumb">
          <button 
            onClick={() => navigate('/')}
            className={`hover:text-blue-600 transition-colors ${isDark ? 'hover:text-blue-400' : ''}`}
          >
            Home
          </button>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <button 
            onClick={() => navigate('/products')}
            className={`hover:text-blue-600 transition-colors ${isDark ? 'hover:text-blue-400' : ''}`}
          >
            Products
          </button>
          {product.category && (() => {
            // Find the category object
            const category = categories.find(c => c.name === product.category);
            if (!category) return null;
            
            // Check if it's a sub-category
            const parentCategory = categories.find(c => c.id === category.parent_id);
            
            return (
              <>
                {parentCategory && (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <button 
                      onClick={() => navigate(`/category/${parentCategory.slug}`)}
                      className={`hover:text-blue-600 transition-colors ${isDark ? 'hover:text-blue-400' : ''}`}
                    >
                      {parentCategory.name}
                    </button>
                  </>
                )}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <button 
                  onClick={() => navigate(parentCategory ? `/category/${parentCategory.slug}/${category.slug}` : `/category/${category.slug}`)}
                  className={`hover:text-blue-600 transition-colors ${isDark ? 'hover:text-blue-400' : ''}`}
                >
                  {category.name}
                </button>
              </>
            );
          })()}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className={`${isDark ? 'text-gray-300' : 'text-gray-900'} font-medium`}>
            {product.name}
          </span>
        </nav>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Product Images Gallery */}
          <div>
            {product.images && product.images.length > 0 ? (
              <div className="space-y-4">
                {/* Main Image Display */}
                <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} aspect-square rounded-lg flex items-center justify-center border shadow-md relative`}>
                  <ImageThumbnail
                    imageUrl={getImageUrl(product.images[currentImageIndex])}
                    altText={`${product.name} - Image ${currentImageIndex + 1}`}
                    title={`${product.name} - Product Image ${currentImageIndex + 1} of ${product.images.length}`}
                    className="max-w-full max-h-full object-contain rounded-lg"
                    images={product.images.map(img => getImageUrl(img))}
                    imageIndex={currentImageIndex}
                  />
                  
                  {/* Image Navigation (only show if multiple images) */}
                  {product.images.length > 1 && (
                    <>
                      {/* Previous Button */}
                      <button
                        onClick={() => setCurrentImageIndex(prev => prev > 0 ? prev - 1 : product.images.length - 1)}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-blue-600 bg-opacity-80 hover:bg-opacity-100 text-white p-2 rounded-full transition-all duration-200 shadow-lg"
                        title="Previous image"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      
                      {/* Next Button */}
                      <button
                        onClick={() => setCurrentImageIndex(prev => prev < product.images.length - 1 ? prev + 1 : 0)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-600 bg-opacity-80 hover:bg-opacity-100 text-white p-2 rounded-full transition-all duration-200 shadow-lg"
                        title="Next image"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      
                      {/* Image Counter */}
                      <div className="absolute bottom-2 right-2 bg-blue-600 bg-opacity-80 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {currentImageIndex + 1} of {product.images.length}
                      </div>
                    </>
                  )}
                </div>
                
                {/* Image Thumbnails (only show if multiple images) */}
                {product.images.length > 1 && (
                  <div className="flex space-x-2 overflow-x-auto pb-2">
                    {product.images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`flex-shrink-0 w-16 h-16 rounded border-2 overflow-hidden transition-all duration-200 ${
                          currentImageIndex === index 
                            ? 'border-blue-500 ring-2 ring-blue-300' 
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        title={`View image ${index + 1}`}
                      >
                        <img
                          src={getImageUrl(image)}
                          alt={`${product.name} thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} aspect-square rounded-lg flex items-center justify-center border shadow-md`}>
                <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>No image available</span>
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            {/* Product Header */}
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
              <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>{product.name}</h1>
              <div 
                className={`product-description-content product-description-full ${isDark ? 'text-gray-300' : 'text-gray-600'} mt-2`}
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
              <p className="text-3xl font-bold mt-4 text-blue-600">${calculatePrice().toFixed(2)}</p>
            </div>

            {/* Product Options */}
            {Object.entries(product.base_options || {}).map(([optionName, options]) => (
              <div key={optionName} className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-4`}>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{optionName}</label>
                <select
                  value={selectedOptions[optionName] || ''}
                  onChange={(e) => setSelectedOptions({...selectedOptions, [optionName]: e.target.value})}
                  className={`w-full px-3 py-2 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                >
                  {options.map((option) => (
                    <option key={option.name} value={option.name}>
                      {option.name} {option.price > 0 && `(+$${option.price.toFixed(2)})`}
                    </option>
                  ))}
                </select>
              </div>
            ))}

            {/* Custom Text Inputs */}
            {Object.entries(product.custom_options || {}).map(([fieldName, config]) => (
              <div key={fieldName} className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-4`}>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {fieldName} {config.required && <span className="text-red-500">*</span>}
                  {config.price > 0 && <span className="text-green-600"> (+${config.price.toFixed(2)})</span>}
                </label>
                {config.input_type === 'textarea' ? (
                  <textarea
                    value={customTextInputs[fieldName] || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!config.character_limit || value.length <= config.character_limit) {
                        setCustomTextInputs({...customTextInputs, [fieldName]: value});
                      }
                    }}
                    placeholder={config.placeholder}
                    required={config.required}
                    className={`w-full px-3 py-2 ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-700'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    rows={3}
                  />
                ) : (
                  <input
                    type="text"
                    value={customTextInputs[fieldName] || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!config.character_limit || value.length <= config.character_limit) {
                        setCustomTextInputs({...customTextInputs, [fieldName]: value});
                      }
                    }}
                    placeholder={config.placeholder}
                    required={config.required}
                    className={`w-full px-3 py-2 ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-700'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                )}
                {config.character_limit && (
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                    {(customTextInputs[fieldName] || '').length} / {config.character_limit} characters
                  </p>
                )}
              </div>
            ))}

            {/* File Upload Section (with drag-and-drop) */}
            {product.file_upload_enabled && (
              <div data-file-upload-section className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
                <div className="flex items-center justify-between mb-3">
                  <label className={`block text-base font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    📎 Upload Your Files {product.file_upload_required && <span className="text-red-500">*</span>}
                  </label>
                  {uploadedFiles.length > 0 && (
                    <span className={`text-sm px-3 py-1 rounded-full ${isDark ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'}`}>
                      {uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} uploaded
                    </span>
                  )}
                </div>

                {product.file_upload_required && (
                  <div className={`mb-3 p-3 rounded-lg ${isDark ? 'bg-amber-900/30 border-amber-700' : 'bg-amber-50 border-amber-200'} border`}>
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <p className={`text-sm ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                        <strong>Required:</strong> You must upload at least one file to add this product to your cart.
                      </p>
                    </div>
                  </div>
                )}

                {/* Drag and Drop Zone */}
                <label className="block">
                  <div
                    className={`cursor-pointer px-6 py-10 border-2 border-dashed rounded-lg text-center transition-all duration-200 ${
                      uploading 
                        ? 'opacity-50 cursor-not-allowed' 
                        : isDark 
                          ? 'border-gray-600 hover:border-blue-500 hover:bg-gray-700/50' 
                          : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                    }`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (!uploading) {
                        e.currentTarget.classList.add(isDark ? 'border-blue-500' : 'border-blue-500', isDark ? 'bg-gray-700' : 'bg-blue-50');
                      }
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-blue-500', isDark ? 'bg-gray-700' : 'bg-blue-50');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-blue-500', isDark ? 'bg-gray-700' : 'bg-blue-50');
                      if (!uploading) {
                        Array.from(e.dataTransfer.files).forEach(file => {
                          handleFileUpload(file);
                        });
                      }
                    }}
                  >
                    <input
                      type="file"
                      multiple
                      accept={product.allowed_file_types || "image/*,.pdf,.stl,.obj,.zip,.rar"}
                      onChange={(e) => {
                        Array.from(e.target.files).forEach(file => {
                          handleFileUpload(file);
                        });
                        e.target.value = null; // Reset input
                      }}
                      className="hidden"
                      disabled={uploading}
                    />
                    
                    <div className="flex flex-col items-center gap-3">
                      {uploading ? (
                        <>
                          <svg className="animate-spin w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <p className={`text-base font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                            Uploading your files...
                          </p>
                        </>
                      ) : (
                        <>
                          <svg className={`w-16 h-16 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <div>
                            <span className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              <span className={`font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Click to upload</span> or drag and drop
                            </span>
                            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                              {(() => {
                                const types = product.allowed_file_types || 'image/*,.pdf,.stl,.obj,.zip,.rar';
                                const typeLabels = [];
                                if (types.includes('image/*')) typeLabels.push('Images');
                                if (types.includes('.pdf')) typeLabels.push('PDFs');
                                if (types.includes('.stl') || types.includes('.obj')) typeLabels.push('3D files');
                                if (types.includes('.zip') || types.includes('.rar')) typeLabels.push('Archives');
                                if (types === '*') return 'Any file type accepted';
                                return typeLabels.length > 0 ? typeLabels.join(', ') : 'Files';
                              })()}
                            </p>
                          </div>
                          <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} mt-1`}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>Max {product.max_file_size_mb || 10}MB per file • Up to {product.max_files_allowed || 10} files</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </label>

                {/* Uploaded Files Display */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-4">
                    <p className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Uploaded Files ({uploadedFiles.length})
                    </p>
                    <div className="grid grid-cols-1 gap-2">
                      {uploadedFiles.map((file, index) => (
                        <div
                          key={index}
                          className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border`}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={`flex-shrink-0 w-10 h-10 rounded flex items-center justify-center ${isDark ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                              <svg className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                                {file.filename || file.original_name || `File ${index + 1}`}
                              </p>
                              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Ready to upload with order
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
                              toast.success('File removed');
                            }}
                            className={`flex-shrink-0 ml-2 p-2 rounded-full ${isDark ? 'hover:bg-red-900/30 text-red-400' : 'hover:bg-red-100 text-red-600'} transition-colors`}
                            title="Remove file"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Addon Products */}
            {product.addons && product.addons.length > 0 && (
              <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-4`}>
                <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Add-ons ({product.addons.length} available)
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {product.addons.map((addon) => {
                    const isSelected = selectedAddons.includes(addon.id);
                    // Get first image from preview_images array
                    const addonThumbnail = addon.preview_images && addon.preview_images.length > 0 
                      ? addon.preview_images[0]
                      : null;
                    
                    return (
                      <div
                        key={addon.id}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedAddons(selectedAddons.filter(id => id !== addon.id));
                          } else {
                            setSelectedAddons([...selectedAddons, addon.id]);
                          }
                        }}
                        className={`relative cursor-pointer rounded-lg border-2 transition-all duration-200 ${
                          isSelected 
                            ? 'border-blue-500 ring-2 ring-blue-300 bg-blue-50 dark:bg-blue-900/20' 
                            : isDark 
                              ? 'border-gray-600 hover:border-gray-500 bg-gray-700' 
                              : 'border-gray-200 hover:border-gray-300 bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        {/* Selection Indicator */}
                        <div className="absolute top-2 right-2 z-10">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                            isSelected 
                              ? 'bg-blue-500 border-blue-500' 
                              : isDark 
                                ? 'bg-gray-600 border-gray-500' 
                                : 'bg-white border-gray-300'
                          }`}>
                            {isSelected && (
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>

                        {/* Addon Content */}
                        <div className="p-3">
                          {/* Thumbnail with Click to Enlarge */}
                          <div className="relative mb-2">
                            {addonThumbnail ? (
                              <div 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Create modal overlay
                                  const modal = document.createElement('div');
                                  modal.className = 'fixed inset-0 z-50 flex items-center justify-center p-4';
                                  modal.style.backgroundColor = 'rgba(0, 0, 0, 0.85)';
                                  modal.onclick = () => document.body.removeChild(modal);
                                  
                                  const imageUrl = getImageUrl(addonThumbnail);
                                  modal.innerHTML = `
                                    <div class="relative max-w-5xl w-full" onclick="event.stopPropagation()">
                                      <button 
                                        onclick="document.body.removeChild(this.closest('.fixed'))"
                                        class="absolute -top-12 right-0 bg-white text-gray-800 rounded-full p-2 hover:bg-gray-200 transition-colors shadow-lg z-10"
                                        title="Close"
                                      >
                                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                      <img 
                                        src="${imageUrl}" 
                                        alt="${addon.name}" 
                                        class="w-full h-auto max-h-[85vh] object-contain rounded-lg shadow-2xl bg-white"
                                      />
                                      <p class="text-center text-white mt-3 text-lg font-medium">${addon.name}</p>
                                    </div>
                                  `;
                                  document.body.appendChild(modal);
                                }}
                                className="group relative aspect-square rounded-md overflow-hidden bg-gray-200 dark:bg-gray-600 cursor-zoom-in"
                              >
                                <img
                                  src={getImageUrl(addonThumbnail)}
                                  alt={addon.name}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-200"
                                />
                                {/* Hover overlay with zoom icon */}
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                                  <div className="transform scale-0 group-hover:scale-100 transition-transform duration-200 bg-white rounded-full p-2 shadow-lg">
                                    <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className={`aspect-square rounded-md flex items-center justify-center ${isDark ? 'bg-gray-600' : 'bg-gray-200'}`}>
                                <span className="text-4xl">📦</span>
                              </div>
                            )}
                          </div>

                          {/* Addon Info */}
                          <div className="space-y-1">
                            <p className={`text-sm font-medium line-clamp-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                              {addon.name}
                            </p>
                            {addon.description && (
                              <p className={`text-xs line-clamp-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                {addon.description}
                              </p>
                            )}
                            <p className="text-sm font-bold text-green-600">
                              +${addon.price.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Selected Addons Summary */}
                {selectedAddons.length > 0 && (
                  <div className={`mt-3 p-3 rounded-lg ${isDark ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'} border`}>
                    <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                      ✓ {selectedAddons.length} addon{selectedAddons.length > 1 ? 's' : ''} selected
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Add to Cart Button */}
            <button
              onClick={handleAddToCart}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Add to Cart - ${calculatePrice().toFixed(2)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Password Reset Page Component
const PasswordResetPage = () => {
  const { isDark } = useTheme();
  const { settings } = useApp();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Get token from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');
    
    if (resetToken) {
      setToken(resetToken);
      validateToken(resetToken);
    } else {
      toast.error('No reset token provided');
      setTimeout(() => navigate('/login'), 2000);
    }
  }, []); // Empty dependency array - only run once on mount

  const validateToken = async (resetToken) => {
    try {
      setValidatingToken(true);
      const response = await axios.post(`${API}/auth/validate-reset-token`, {
        token: resetToken
      });
      
      if (response.data.valid) {
        setTokenValid(true);
        setUserEmail(response.data.email);
        toast.success('Reset link validated successfully');
      } else {
        setTokenValid(false);
        toast.error('Invalid or expired reset link');
        setTimeout(() => window.location.href = '/login', 3000);
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      setTokenValid(false);
      toast.error(error.response?.data?.message || 'Invalid reset link');
      setTimeout(() => window.location.href = '/login', 3000);
    } finally {
      setValidatingToken(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (password !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }

      const response = await axios.post(`${API}/auth/password-reset/confirm`, {
        token: token,
        password: password
      });
      
      if (response.data.success) {
        toast.success('Password reset successful! You can now log in with your new password.');
        navigate('/login');
      }
    } catch (error) {
      console.error('Password reset failed:', error);
      toast.error(error.response?.data?.detail || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'} flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Validating reset link...</h2>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'} flex items-center justify-center`}>
        <div className="text-center">
          <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-800'} mb-4`}>Invalid Reset Link</h2>
          <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`}>This password reset link is invalid or has expired.</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'} flex items-center justify-center py-12 px-4`}>
      <div className="max-w-md w-full">
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-8`}>
          <div className="text-center mb-8">
            {settings?.company_settings?.logo_url && (
              <img 
                src={settings.company_settings.logo_url} 
                alt="RG-3D Logo" 
                className="w-16 h-16 mx-auto mb-4"
              />
            )}
            <h2 className="text-3xl font-bold text-gray-800">Reset Password</h2>
            <p className="text-gray-600 mt-2">Create a new password for {userEmail}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Confirm your new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>Password Requirements:</strong><br />
                • At least 8 characters long<br />
                • Include uppercase and lowercase letters<br />
                • Include at least one number<br />
                • Avoid common weak patterns
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors duration-200"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Account Completion Page Wrapper
// Complete Signup Page for customers invited by admin
const CompleteSignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useApp();  // Get login function from context
  const [loading, setLoading] = useState(false);
  const [checkingAccount, setCheckingAccount] = useState(true);
  const [accountExists, setAccountExists] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    phone: ''
  });
  const [loginData, setLoginData] = useState({
    password: ''
  });
  const [error, setError] = useState('');
  const [orderInfo, setOrderInfo] = useState(null);
  
  // Get token and order_id from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get('token');
  const orderId = urlParams.get('order_id');

  useEffect(() => {
    if (!token || !orderId) {
      toast.error('Invalid or expired order link');
      navigate('/');
      return;
    }
    
    // Load order information and check if customer account exists
    const loadOrderInfoAndCheckAccount = async () => {
      try {
        // First, load order information
        const response = await axios.get(`${API}/orders/${orderId}/preview`, {
          params: { token }
        });
        setOrderInfo(response.data);
        
        // Check if customer account already exists
        try {
          const checkResponse = await axios.post(`${API}/auth/check-email`, {
            email: response.data.customer_email
          });
          
          if (checkResponse.data.exists) {
            setAccountExists(true);
            setShowLoginForm(true);
          } else {
            setAccountExists(false);
          }
        } catch (checkError) {
          // If check fails, assume account doesn't exist
          setAccountExists(false);
        }
        
      } catch (error) {
        console.error('Failed to load order info:', error);
        toast.error('Invalid or expired order link');
        navigate('/');
      } finally {
        setCheckingAccount(false);
      }
    };
    
    loadOrderInfoAndCheckAccount();
  }, [token, orderId, navigate]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleLoginInputChange = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const loginResponse = await axios.post(`${API}/auth/login`, {
        email: orderInfo.customer_email,
        password: loginData.password
      });

      if (loginResponse.status === 200) {
        // Use context login function to properly update app state
        login(loginResponse.data.user, loginResponse.data.access_token);
        toast.success('Welcome back! You can now view your order.');
        navigate('/account');
      }
    } catch (error) {
      console.error('Login failed:', error);
      setError('Invalid password. Please try again or contact support if you forgot your password.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      // Complete the signup
      const response = await axios.post(`${API}/complete-signup`, {
        token,
        order_id: orderId,
        password: formData.password,
        phone: formData.phone
      });

      if (response.status === 200) {
        // Auto-login the user
        const loginResponse = await axios.post(`${API}/auth/login`, {
          email: orderInfo.customer_email,
          password: formData.password
        });

        if (loginResponse.status === 200) {
          // Use context login function to properly update app state
          login(loginResponse.data.user, loginResponse.data.access_token);
          toast.success('Account created successfully! Welcome to your account.');
          navigate('/account');
        }
      }
    } catch (error) {
      console.error('Signup completion failed:', error);
      setError(error.response?.data?.detail || 'Failed to complete signup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token || !orderId) {
    return null;
  }

  if (checkingAccount) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your order information...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            {accountExists ? (
              <>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  🔑 Welcome Back!
                </h2>
                <p className="text-gray-600 mb-6">
                  We found an existing account for {orderInfo?.customer_email}. Please log in to view your order.
                </p>
              </>
            ) : (
              <>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  🎉 Complete Your Account
                </h2>
                <p className="text-gray-600 mb-6">
                  Create your account to access your order
                </p>
              </>
            )}
          </div>

          {orderInfo && (
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">📦 Your Order</h3>
              <div className="text-sm text-blue-800">
                <p><strong>Customer:</strong> {orderInfo.customer_name}</p>
                <p><strong>Email:</strong> {orderInfo.customer_email}</p>
                <p><strong>Product:</strong> {orderInfo.product_name}</p>
                <p><strong>Total:</strong> ${orderInfo.total}</p>
                <p><strong>Status:</strong> {orderInfo.order_status}</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {accountExists ? (
            // Login form for existing users
            <div>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label htmlFor="loginPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    id="loginPassword"
                    name="password"
                    required
                    value={loginData.password}
                    onChange={handleLoginInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Signing In...' : '🔓 Sign In & View Order'}
                </button>
              </form>

              <div className="mt-4 text-center">
                <button
                  onClick={() => {
                    setAccountExists(false);
                    setShowLoginForm(false);
                    setError('');
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Not your account? Create a new account instead
                </button>
              </div>
            </div>
          ) : (
            // Signup form for new users
            <div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <p className="text-green-700 text-sm">
                  <strong>Account Details (Pre-filled):</strong><br/>
                  Name: {orderInfo?.customer_name}<br/>
                  Email: {orderInfo?.customer_email}
                </p>
              </div>

              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Create Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your password"
                    minLength="6"
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Confirm your password"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number (Optional)
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your phone number"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating Account...' : '✨ Complete Account & View Order'}
                </button>
              </form>

              <div className="mt-4 text-center">
                <button
                  onClick={() => {
                    setAccountExists(true);
                    setShowLoginForm(true);
                    setError('');
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Already have an account? Sign in instead
                </button>
              </div>
            </div>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              After {accountExists ? 'signing in' : 'creating your account'}, you'll be able to:
            </p>
            <ul className="text-sm text-gray-500 mt-2 space-y-1">
              <li>• View your order details and tracking</li>
              <li>• Make payments on outstanding balances</li>
              <li>• Update shipping preferences</li>
              <li>• Access your order history</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const AccountCompletePage = () => {
  const { user } = useApp();
  const navigate = useNavigate();

  if (!user) {
    navigate('/login');
    return null;
  }

  const handleComplete = () => {
    navigate('/');
  };

  return <AccountCompletion user={user} onComplete={handleComplete} />;
};

// Category Page Component
const CategoryPage = () => {
  const location = useLocation();
  const { isDark } = useTheme();
  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  // Extract category path from URL (everything after /category/)
  const categoryPath = location.pathname.replace('/category/', '');

  useEffect(() => {
    if (categoryPath) {
      loadCategoryAndProducts();
      loadCategories();
    }
  }, [categoryPath]);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadCategoryAndProducts = async () => {
    try {
      setLoading(true);
      const [categoryResponse, productsResponse] = await Promise.all([
        axios.get(`${API}/categories/${categoryPath}`),
        axios.get(`${API}/category/${categoryPath}/products`)
      ]);
      
      setCategory(categoryResponse.data);
      setProducts(productsResponse.data);
    } catch (error) {
      console.error('Failed to load category:', error);
      toast.error('Category not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const handleViewProduct = (productId) => {
    navigate(`/product/${productId}`);
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'} flex items-center justify-center`}>
        <div className={isDark ? 'text-gray-300' : 'text-gray-600'}>Loading category...</div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'}`}>
        <div className="max-w-7xl mx-auto px-4 py-8 text-center">
          <h1 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Category Not Found</h1>
          <button
            onClick={() => navigate('/products')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
          >
            Browse All Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'}`}>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb Navigation */}
        <nav className={`flex items-center space-x-2 text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} aria-label="Breadcrumb">
          <button 
            onClick={() => navigate('/')}
            className={`hover:text-blue-600 transition-colors ${isDark ? 'hover:text-blue-400' : ''}`}
          >
            Home
          </button>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <button 
            onClick={() => navigate('/products')}
            className={`hover:text-blue-600 transition-colors ${isDark ? 'hover:text-blue-400' : ''}`}
          >
            Products
          </button>
          {category.parent_id && (() => {
            // Find parent category
            const parentCategory = categories.find(c => c.id === category.parent_id);
            if (!parentCategory) return null;
            
            return (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <button 
                  onClick={() => navigate(`/category/${parentCategory.slug}`)}
                  className={`hover:text-blue-600 transition-colors ${isDark ? 'hover:text-blue-400' : ''}`}
                >
                  {parentCategory.name}
                </button>
              </>
            );
          })()}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className={`${isDark ? 'text-gray-300' : 'text-gray-900'} font-medium`}>
            {category.name}
          </span>
        </nav>

        {/* Category Header */}
        <div className="mb-8 text-center">
          <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>{category.name}</h1>
          {category.description && (
            <p className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'} max-w-2xl mx-auto`}>{category.description}</p>
          )}
        </div>

        {/* Products Grid */}
        {products.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.map((product) => (
              <div 
                key={product.id} 
                className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border cursor-pointer transform hover:scale-105`}
                onClick={() => handleViewProduct(product.id)}
              >
                <div className={`${isDark ? 'bg-gray-700' : 'bg-gray-100'} aspect-square rounded-t-xl flex items-center justify-center`}>
                  {product.images && product.images.length > 0 ? (
                    <img 
                      src={getImageUrl(product.images[0])} 
                      alt={product.name} 
                      className="max-w-full max-h-full object-contain rounded-t-xl" 
                    />
                  ) : (
                    <span className="text-gray-400">No image available</span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold mb-2 text-gray-800">{product.name}</h3>
                  <div 
                    className="product-description-content product-description-preview text-gray-600 mb-4 text-sm"
                    dangerouslySetInnerHTML={{ __html: product.description }}
                  />
                  
                  {/* Feature badges */}
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(product.base_options || {}).length > 0 && (
                      <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                        Options Available
                      </span>
                    )}
                    {Object.keys(product.custom_options || {}).length > 0 && (
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
                        Customizable
                      </span>
                    )}
                    {(product.addons || []).length > 0 && (
                      <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium">
                        Addons Available
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h2 className="text-2xl font-semibold text-gray-700 mb-2">No Products Found</h2>
            <p className="text-gray-600 mb-6">This category doesn't have any products yet.</p>
            <button
              onClick={() => navigate('/products')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors duration-200"
            >
              Browse All Products
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Themed HomePage Wrapper
const ThemedHomePage = () => {
  const { isDark, toggleTheme } = useTheme();
  const { user } = useApp();
  return <PremiumHomePage isDark={isDark} toggleTheme={toggleTheme} user={user} />;
};

// Main App Component
function App() {
  // PayPal configuration - will be loaded from settings
  const [paypalOptions, setPaypalOptions] = React.useState(null);
  const [paypalLoading, setPaypalLoading] = React.useState(true);

  React.useEffect(() => {
    // Fetch PayPal settings
    const fetchPayPalSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/public`);
        const settings = await response.json();
        
        console.log('PayPal settings loaded:', {
          enabled: settings.paypal_enabled,
          clientId: settings.paypal_client_id?.substring(0, 20) + '...',
          environment: settings.paypal_environment,
          mode: settings.paypal_mode
        });
        
        if (settings.paypal_enabled && settings.paypal_client_id) {
          // Only set options if we have valid credentials
          setPaypalOptions({
            'client-id': settings.paypal_client_id,
            currency: settings.currency || 'CAD',
            intent: 'capture',
            'enable-funding': 'venmo,paylater',
            'data-sdk-integration-source': 'button-factory'
          });
        } else {
          console.warn('PayPal not enabled or no client ID found');
        }
      } catch (error) {
        console.error('Failed to load PayPal settings:', error);
      } finally {
        setPaypalLoading(false);
      }
    };

    fetchPayPalSettings();
  }, []);

  // Don't render until PayPal settings are loaded
  if (paypalLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      {paypalOptions ? (
        <PayPalScriptProvider options={paypalOptions}>
          <AppProvider>
            <div className="App min-h-screen bg-gray-50">
              <BrowserRouter>
                <MaintenanceChecker />
              </BrowserRouter>
            </div>
          </AppProvider>
        </PayPalScriptProvider>
      ) : (
        <AppProvider>
          <div className="App min-h-screen bg-gray-50">
            <BrowserRouter>
              <MaintenanceChecker />
            </BrowserRouter>
          </div>
        </AppProvider>
      )}
    </ThemeProvider>
  );
}

// Protected Route Component - Requires user authentication
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useApp();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Redirect to login page with return URL
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Admin Route Component - Requires admin authentication
const AdminRoute = ({ children }) => {
  const { user, loading } = useApp();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Redirect to login page with return URL
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!user.is_admin) {
    // Redirect to home if not admin
    return <Navigate to="/" replace />;
  }

  return children;
};

// Component to check maintenance mode and render appropriate content
const MaintenanceChecker = () => {
  const { maintenanceMode, maintenanceMessage, user } = useApp();
  const location = useLocation();

  // Always allow admin login page, even during maintenance
  const isAdminLoginPage = location.pathname === '/admin/login' || location.pathname.startsWith('/admin/login');

  // Show maintenance page for non-admin users when maintenance mode is on
  // BUT allow access to admin login page
  if (maintenanceMode && (!user || !user.is_admin) && !isAdminLoginPage) {
    return <MaintenancePage message={maintenanceMessage} />;
  }

  // Normal app rendering
  return (
    <>
      <ScrollToTop />
      <Header />
      <main>
        <Routes>
          <Route path="/" element={<ThemedHomePage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/ai-products" element={<ProtectedRoute><AIProductsPage /></ProtectedRoute>} />
          <Route path="/ai-products/lithophane-sphere" element={<LithophaneSpherePage />} />
          <Route path="/product/:productId" element={<ProductDetailPage />} />
          <Route path="/category/*" element={<CategoryPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<CreateAccountPage />} />
          <Route path="/reset-password" element={<PasswordResetPage />} />
          <Route path="/account/complete" element={<AccountCompletePage />} />
          <Route path="/complete-signup" element={<CompleteSignupPage />} />
          <Route path="/account" element={<CustomerAccount />} />
          <Route path="/policies/:slug" element={<PolicyPage />} />
          <Route path="/admin" element={<AdminPortal />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <CookieConsent />
      <Toaster position="top-right" />
    </>
  );
};

export default App;
export { useApp };